import { acquireIdToken, DEV_BYPASS_AUTH, renovarSesion } from "./auth";

/**
 * fetch AUTENTICADO — el ÚNICO camino al backend.
 *
 * - Obtiene el id_token y PROPAGA el error si falla (nunca se manda una
 *   petición sin Authorization; el catch silencioso viejo escondía sesiones
 *   muertas tras errores 401 confusos). Solo en dev-bypass va sin header.
 * - Si el backend responde 401, dispara renovarSesion() (redirect a Entra,
 *   con guarda anti-loop) antes de regresar la respuesta.
 * - No fija Content-Type: cada llamador pone el suyo (multipart necesita que
 *   el navegador arme el boundary solo).
 */
export async function fetchAuth(url: string, init: RequestInit = {}): Promise<Response> {
  const headers = new Headers(init.headers);
  if (!DEV_BYPASS_AUTH) {
    headers.set("Authorization", `Bearer ${await acquireIdToken()}`);
  }
  const res = await window.fetch(url, { credentials: "include", ...init, headers });
  if (res.status === 401) renovarSesion();
  return res;
}

async function detalleDe(res: Response): Promise<string> {
  let detail = `${res.status} ${res.statusText}`.trim();
  try {
    const j = await res.json();
    if (j?.detail) detail = typeof j.detail === "string" ? j.detail : JSON.stringify(j.detail);
  } catch {
    // ignore
  }
  return detail;
}

export async function jsonFetch<T>(url: string, init?: RequestInit): Promise<T> {
  const res = await fetchAuth(url, init);
  if (!res.ok) throw new Error(await detalleDe(res));
  return res.json();
}

async function jsonPost<T>(url: string, body: unknown): Promise<T> {
  const res = await fetchAuth(url, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  if (!res.ok) throw new Error(await detalleDe(res));
  return res.json();
}

async function jsonPut<T>(url: string, body: unknown): Promise<T> {
  const res = await fetchAuth(url, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  if (!res.ok) throw new Error(await detalleDe(res));
  return res.json();
}

export type Me = {
  email: string;
  name: string;
  rol: string | null;
  permisos: string[];
};

export function getMe(): Promise<Me> {
  return jsonFetch<Me>("/api/me");
}

// ---------- HSC (Houston Ship Channel) para el widget del encabezado ---------

export type HscUltimo = {
  fecha: string | null;
  valor: number | null;
  cambio: number | null;
  /** false = el dato no es del día hábil anterior o más reciente (ETL atrasado). */
  al_dia: boolean;
};

export function getHscUltimo(): Promise<HscUltimo> {
  return jsonFetch<HscUltimo>("/api/etrm/hsc");
}

/** Semáforo REA (mismo dato que el Portal: public.monitoreo_rea). */
export type ReaUltimo = {
  pct: number | null;
  color: string | null; // "rgb(0, 200, 83)" — ya viene calculado por el monitor
  semaforo: string | null; // NORMAL / …
  creado_en: string | null;
  stale: boolean; // más de 12 h sin actualizar
};

export function getReaUltimo(): Promise<ReaUltimo> {
  return jsonFetch<ReaUltimo>("/api/etrm/rea");
}

/** Captura manual del HSC (permiso 'capturar_hsc'; para cuando el ETL está atrasado). */
export function capturarHsc(
  fecha: string,
  valor: number,
): Promise<{ fecha: string; valor: number; guardado: boolean }> {
  return jsonPost("/api/etrm/hsc", { fecha, valor });
}

// ---------- Administración de perfiles (RBAC) -------------------------------

export type RolInfo = { key: string; label: string; editable: boolean };
export type PermisoInfo = { key: string; label: string };
export type AdminConfig = {
  roles: RolInfo[];
  permisos: PermisoInfo[];
  matriz: Record<string, Record<string, boolean>>;
};
export type UsuarioRol = {
  email: string;
  rol: string;
  notificar_tbfin: boolean;
  actualizado_en: string | null;
  actualizado_por: string | null;
};

export function getAdminConfig(): Promise<AdminConfig> {
  return jsonFetch<AdminConfig>("/api/etrm/admin/config");
}

export function getAdminUsuarios(): Promise<UsuarioRol[]> {
  return jsonFetch<UsuarioRol[]>("/api/etrm/admin/usuarios");
}

export function asignarRol(email: string, rol: string): Promise<{ email: string; rol: string }> {
  return jsonPut("/api/etrm/admin/usuarios", { email, rol });
}

export function quitarRol(email: string): Promise<{ email: string; eliminado: boolean }> {
  return jsonDelete(`/api/etrm/admin/usuarios/${encodeURIComponent(email)}`);
}

export function setNotificacionTbfin(
  email: string,
  notificarTbfin: boolean,
): Promise<{ email: string; notificar_tbfin: boolean }> {
  return jsonPut("/api/etrm/admin/usuarios/notificacion", {
    email,
    notificar_tbfin: notificarTbfin,
  });
}

export function editarMatriz(
  rol: string,
  permisos: Record<string, boolean>,
): Promise<{ rol: string; permisos: Record<string, boolean> }> {
  return jsonPut("/api/etrm/admin/matriz", { rol, permisos });
}

// ---------- Envío de correo de escenarios (Reporte Comercial diario) ---------

export type EscenarioResumen = {
  id: number;
  nombre: string;
  descripcion: string | null;
  creado_por: string;
  actualizado_en: string;
  n_operaciones: number;
};
export type CorreoEscenariosConfig = {
  activo: boolean;
  id_escenario: number | null;
  destinatarios: string[];
  actualizado_en: string | null;
  actualizado_por: string | null;
};
export type CorreoEscenarios = {
  escenarios: EscenarioResumen[];
  config: CorreoEscenariosConfig;
  guardada: boolean;
  tabla_lista: boolean;
  horario: string;
};

export function getCorreoEscenarios(): Promise<CorreoEscenarios> {
  return jsonFetch<CorreoEscenarios>("/api/etrm/admin/correo-escenarios");
}

export function setCorreoEscenarios(payload: {
  activo: boolean;
  id_escenario: number | null;
  destinatarios: string[];
}): Promise<{ config: CorreoEscenariosConfig; guardada: boolean }> {
  return jsonPut("/api/etrm/admin/correo-escenarios", payload);
}

// ---------- Nodos Cauchy (Configuración) ------------------------------------
// Puntos para los que DART_Cauchy.R calcula location/scale diarios.

export type TipoPuntoCauchy = "nodo" | "zona";
export type PuntoCauchy = {
  id: number;
  etiqueta: string;
  tipo: TipoPuntoCauchy;
  sistema: string;
  clave: string;
  activo: boolean;
  actualizado_por: string | null;
  actualizado_en: string;
  dias: number | null;
  desde: string | null;
  hasta: string | null;
  ultimo_location: number | null;
  ultimo_scale: number | null;
  // Parámetros del VaR DART del arbitraje (mig 110).
  var_lh_min: number;
  var_lh_max: number;
  var_tc: number;
  var_confianza: number;   // 0.97
  // Versiones con vigencia (mig 114), la más nueva primero.
  var_historial: VersionParamsCauchy[];
};
export type VersionParamsCauchy = {
  vigente_desde: string;   // 1900-01-01 = desde siempre
  lh_min: number; lh_max: number; tc: number; confianza: number;
  registrado_por: string | null;
};
export type OpcionPuntoCauchy = { clave: string; nombre: string | null; zona_carga?: string | null };

const NODOS_CAUCHY = "/api/etrm/admin/nodos-cauchy";

export function getNodosCauchy(): Promise<{ puntos: PuntoCauchy[]; ventana_dias: number }> {
  return jsonFetch(NODOS_CAUCHY);
}

export function getOpcionesNodosCauchy(
  tipo: TipoPuntoCauchy, sistema: string, q: string,
): Promise<OpcionPuntoCauchy[]> {
  const p = new URLSearchParams({ tipo, sistema, q });
  return jsonFetch(`${NODOS_CAUCHY}/opciones?${p}`);
}

export function addNodoCauchy(payload: {
  etiqueta: string; tipo: TipoPuntoCauchy; sistema: string; clave: string;
}): Promise<{ id: number }> {
  return jsonPost(NODOS_CAUCHY, payload);
}

export function updateNodoCauchy(
  id: number, payload: {
    etiqueta?: string; activo?: boolean;
    var_lh_min?: number; var_lh_max?: number; var_tc?: number; var_confianza?: number;
  },
): Promise<{ id: number }> {
  return jsonPut(`${NODOS_CAUCHY}/${id}`, payload);
}

export function deleteNodoCauchy(id: number): Promise<{ id: number; eliminado: boolean }> {
  return jsonDelete(`${NODOS_CAUCHY}/${id}`);
}

// ---------- Parámetros Cauchy (Reportes) ------------------------------------

export type PuntoSerieCauchy = {
  tipo: TipoPuntoCauchy;
  sistema: string;
  clave: string;
  etiqueta: string;
  activo: boolean;
  dias: number;
  desde: string;
  hasta: string;
};
export type FilaSerieCauchy = {
  tipo: TipoPuntoCauchy;
  sistema: string;
  clave: string;
  fecha: string;
  location: number;
  scale: number;
  se_location: number | null;
  se_scale: number | null;
  n_obs: number;
  n_usadas: number;
};

/** Llave de un punto en los parámetros del API: "tipo|sistema|clave". */
export const llaveCauchy = (p: { tipo: string; sistema: string; clave: string }) =>
  `${p.tipo}|${p.sistema}|${p.clave}`;

function paramsCauchy(puntos: string[], desde: string, hasta: string): URLSearchParams {
  const p = new URLSearchParams();
  puntos.forEach((x) => p.append("puntos", x));
  if (desde) p.set("desde", desde);
  if (hasta) p.set("hasta", hasta);
  return p;
}

/** FIX USD/MXN vigente en la fecha (permiso parametros_cauchy). */
export function getFixCauchy(fecha: string): Promise<{ fecha: string | null; valor: number | null }> {
  return jsonFetch(`/api/etrm/cauchy/fix?fecha=${fecha}`);
}

export function getPuntosCauchy(): Promise<{ ventana_dias: number; puntos: PuntoSerieCauchy[] }> {
  return jsonFetch("/api/etrm/cauchy/puntos");
}

export function getSerieCauchy(
  puntos: string[], desde: string, hasta: string,
): Promise<{ ventana_dias: number; serie: FilaSerieCauchy[] }> {
  return jsonFetch(`/api/etrm/cauchy/serie?${paramsCauchy(puntos, desde, hasta)}`);
}

export async function descargarSerieCauchyXlsx(
  puntos: string[], desde: string, hasta: string,
): Promise<void> {
  const res = await fetchAuth(`/api/etrm/cauchy/xlsx?${paramsCauchy(puntos, desde, hasta)}`, {
    credentials: "include",
  });
  if (!res.ok) throw new Error(await detalleDe(res));
  const blob = await res.blob();
  const objectUrl = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = objectUrl;
  const disp = res.headers.get("Content-Disposition") ?? "";
  const m = /filename="([^"]+)"/.exec(disp);
  a.download = m ? m[1] : `parametros_cauchy_${desde}_${hasta}.xlsx`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(objectUrl);
}

// ---------- Tarifas Reguladas (TReg) ----------------------------------------

export type ComponenteCatalogo = {
  key: string;
  label: string;
  descripcion: string;
};

export type TarifaReguladaComponente = {
  componente: string;
  valor_mxn_mwh: string;
};

/** Nivel de tensión del generador: define qué tarifa de transmisión aplica. */
export type NivelTension = "lt220" | "ge220";

export type NivelTensionCatalogo = {
  key: NivelTension;
  label: string;
  descripcion: string;
};

export type TarifaReguladaAnio = {
  anio: number;
  nivel_tension: NivelTension;
  componentes: TarifaReguladaComponente[];
  total_mxn_mwh: string;
  notas: string | null;
  actualizado_en: string | null;
  actualizado_por: string | null;
};

export type TarifasReguladasResponse = {
  catalogo: ComponenteCatalogo[];
  niveles: NivelTensionCatalogo[];
  items: TarifaReguladaAnio[];
};

export type TarifaReguladaUpsert = {
  anio: number;
  nivel_tension: NivelTension;
  componentes: { componente: string; valor_mxn_mwh: number }[];
  notas?: string | null;
};

export function getTarifasReguladas(
  anio?: number,
  nivel?: NivelTension,
): Promise<TarifasReguladasResponse> {
  const p = new URLSearchParams();
  if (anio != null) p.set("anio", String(anio));
  if (nivel) p.set("nivel_tension", nivel);
  const qs = p.toString() ? `?${p}` : "";
  return jsonFetch<TarifasReguladasResponse>(`/api/etrm/tarifas-reguladas${qs}`);
}

export function saveTarifaRegulada(
  payload: TarifaReguladaUpsert,
): Promise<TarifaReguladaAnio> {
  return jsonPost<TarifaReguladaAnio>("/api/etrm/tarifas-reguladas", payload);
}

async function jsonDelete<T>(url: string): Promise<T> {
  const res = await fetchAuth(url, { method: "DELETE", credentials: "include" });
  if (!res.ok) throw new Error(`${res.status} ${res.statusText}`);
  return res.json();
}

// ---------- Contratos + parámetros versionados (TBFin) ----------------------

export type VariableDef = {
  nombre: string;
  fuente: string; // constante | bloomberg | cpi_us | tipo_cambio | treg_anual
  valor?: number | null;
  ticker?: string | null;
  lag_dias?: number | null;
  mes_offset?: number | null;
};

export type ParametroVersion = {
  id: number;
  vigente_desde: string;
  variables: VariableDef[];
  formula_strike: string;
  formula_mwh: string;
  notas: string | null;
  actualizado_en: string | null;
  actualizado_por: string | null;
};

export type ContratoBase = {
  id_contrato: number;
  folio: string;
  contraparte: string;
  rol_mpp: string;
  nodo_pml: string;
  central: string | null;
  modalidad: string | null;
  fecha_inicio: string | null;
  fecha_fin: string | null;
  estado: string;
  tipo_precio: string | null;
  moneda: string;
  /** Cláusula de Tarifas Reguladas de la OC: qué TReg aplica (<220 kV vs ≥220 kV). */
  nivel_tension: NivelTension;
  id_contraparte: number | null;
  contraparte_nombre: string | null;
  contraparte_alias: string | null;
  contraparte_cuenta_sim: string | null;
  contraparte_cuenta_orden: string | null;
  correo_contraparte: string | null;
  produccion_inicio: string | null;
  produccion_fin: string | null;
  /** TBFin registrada por la contraparte; CENACE la formaliza sola (celda U en calendario). */
  tbfin_automatica: boolean;
  /** Plazo de pago de la OC (mig 083): días que tiene el comprador para pagar
   *  tras la factura. null = la OC pactó fechas límite fijas. */
  plazo_pago_dias: number | null;
  plazo_pago_habiles: boolean;
};

export type ContratoListItem = ContratoBase & {
  n_versiones: number;
  ultima_vigencia: string | null;
  productos?: string[]; // ["Energía", "Potencia", "CEL"]
  // La ficha maneja factura PRELIMINAR (prepago con gas promedio) + AJUSTE
  // al gas efectivo del mes operado (002MPP/ATR §18/§20).
  prepago_gas?: boolean;
};

export type ContratoDetalle = ContratoBase & {
  parametros: ParametroVersion[];
};

export type ContratoInput = {
  folio: string;
  contraparte: string;
  rol_mpp: string;
  nodo_pml: string;
  central?: string | null;
  modalidad?: string | null;
  fecha_inicio?: string | null;
  fecha_fin?: string | null;
  estado?: string;
  tipo_precio?: string | null;
  moneda?: string;
  nivel_tension?: NivelTension;
  id_contraparte?: number | null;
  correo_contraparte?: string | null;
  produccion_inicio?: string | null;
  produccion_fin?: string | null;
};

export type Contraparte = {
  id_contraparte: number;
  nombre: string;
  alias: string | null;
  id_cuenta_sim: string | null;
  cuenta_orden: string | null;
  notas: string | null;
};

export type ContraparteInput = {
  nombre: string;
  alias?: string | null;
  id_cuenta_sim?: string | null;
  cuenta_orden?: string | null;
  notas?: string | null;
};

export function getContrapartes(): Promise<Contraparte[]> {
  return jsonFetch<Contraparte[]>("/api/etrm/contrapartes");
}

export function crearContraparte(p: ContraparteInput): Promise<Contraparte> {
  return jsonPost<Contraparte>("/api/etrm/contrapartes", p);
}

export async function actualizarContraparte(
  id: number,
  p: ContraparteInput,
): Promise<Contraparte> {
  const res = await fetchAuth(`/api/etrm/contrapartes/${id}`, {
    method: "PUT",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(p),
  });
  if (!res.ok) throw new Error(`${res.status} ${res.statusText}`);
  return res.json();
}

export type CpiPeriodo = { periodo: string; valor: number };

export function getCpiPeriodos(): Promise<CpiPeriodo[]> {
  return jsonFetch<CpiPeriodo[]>("/api/etrm/contratos/cpi-periodos");
}

export type ParametroInput = {
  vigente_desde: string;
  variables: VariableDef[];
  formula_strike: string;
  formula_mwh: string;
  notas?: string | null;
};

export function getContratos(): Promise<{ items: ContratoListItem[] }> {
  return jsonFetch<{ items: ContratoListItem[] }>("/api/etrm/contratos");
}

export type PuntoInventario = {
  periodo: string;
  etiqueta: string;
  compras: number;
  ventas: number;
  inventario: number;
  [serieKey: string]: number | string; // s0, s1, ... por contraparte+lado
};
export type SerieInventario = {
  key: string;
  contraparte: string;
  tipo: "Compra" | "Venta";
  /** true si la serie viene de operaciones hipotéticas de un escenario. */
  esc?: boolean;
};
export type ContratoInventario = {
  folio: string;
  contraparte: string;
  tipo: "Compra" | "Venta";
  cantidad: number;
  /** Volumen de toda la vigencia en `unidad_acumulado` (Energía MWh, Potencia MW-año…). */
  acumulado?: number;
  vigencia_desde: string;
  vigencia_hasta: string;
};
export type ReporteInventario = {
  producto: string;
  agrupacion: string;
  horizonte: number;
  incluir_pasado: boolean;
  energia_agrupada?: boolean;
  /** Energía/Potencia traen líneas de posición corta/larga en cada punto. */
  con_posicion?: boolean;
  unidad: string;
  unidad_contrato: string;
  unidad_acumulado?: string;
  puntos: PuntoInventario[];
  series: SerieInventario[];
  contratos: ContratoInventario[];
  totales: {
    compras: number; ventas: number; inventario: number;
    acumulado_compras?: number; acumulado_ventas?: number;
  };
};

export function getReporteInventario(opts: {
  producto: string;
  agrupacion: string;
  horizonte: number;
  incluirPasado: boolean;
  energiaAgrupada?: boolean;
}): Promise<ReporteInventario> {
  const p = new URLSearchParams();
  p.set("producto", opts.producto);
  p.set("agrupacion", opts.agrupacion);
  p.set("horizonte", String(opts.horizonte));
  p.set("incluir_pasado", String(opts.incluirPasado));
  if (opts.energiaAgrupada != null) p.set("energia_agrupada", String(opts.energiaAgrupada));
  return jsonFetch<ReporteInventario>(`/api/etrm/reportes/inventario?${p.toString()}`);
}

// ---------- Escenarios de compra/venta (what-if, privados por usuario) -------

export type OperacionEscenario = {
  tipo: "compra" | "venta";
  producto: "Energía" | "Potencia" | "CEL" | "i-REC";
  contraparte?: string | null;
  volumen: number;
  precio?: number | null;
  moneda?: string;
  fecha_inicio: string;
  fecha_fin: string;
  /** Inactiva = se guarda con el escenario pero no entra a la simulación. */
  activa?: boolean;
  /** 'vendida'/'cancelada' salen de la gráfica y del PDF (con deshacer). */
  estado?: "activa" | "vendida" | "cancelada";
  fecha_venta?: string | null;
  fecha_cancelacion?: string | null;
  /** Business Case aprobado (solo aplica a operaciones > 1 año). */
  aprobado?: boolean;
  /** Contrato real que respalda la operación (obligatorio para marcar vendida
   *  una venta del escenario que sale en el correo diario). */
  id_contrato?: number | null;
  contrato_folio?: string | null;
};

export type EscenarioCompartido = { email: string; permiso: "ver" | "editar" };

export type Escenario = {
  id: number;
  nombre: string;
  descripcion: string | null;
  operaciones: OperacionEscenario[];
  n_operaciones: number;
  creado_por: string;
  creado_en: string;
  actualizado_en: string;
  /** Qué puede hacer el usuario actual con este escenario. */
  permiso: "dueño" | "editar" | "ver";
  es_mio: boolean;
  /** Con quién lo compartió el dueño (solo viene para el dueño). */
  compartidos: EscenarioCompartido[] | null;
  /** Es el escenario que se envía cada día como Reporte Comercial (Configuración
   *  → Envío de correo de escenarios). Sus ventas exigen contrato vinculado. */
  es_correo?: boolean;
};

/** Contrato real del catálogo /escenarios/contratos (ligero, permiso escenarios). */
export type ContratoVinculable = {
  id_contrato: number;
  folio: string;
  contraparte: string;
  rol_mpp: string;
  modalidad: string | null;
  estado: string;
  fecha_inicio: string | null;
  fecha_fin: string | null;
  productos: string[];
  /** Lado del escenario que respalda: MPP vendedor → 'venta'; comprador → 'compra'. */
  lado: "compra" | "venta" | null;
};

export function getContratosEscenarios(): Promise<ContratoVinculable[]> {
  return jsonFetch<ContratoVinculable[]>("/api/etrm/escenarios/contratos");
}

export type EscenarioInput = {
  nombre: string;
  descripcion?: string | null;
  operaciones: OperacionEscenario[];
};

export type SimulacionEscenario = ReporteInventario & {
  n_ops_simuladas: number;
  n_ops_otros_productos: number;
};

export function getEscenarios(): Promise<{ items: Escenario[] }> {
  return jsonFetch<{ items: Escenario[] }>("/api/etrm/escenarios");
}

export function crearEscenario(payload: EscenarioInput): Promise<Escenario> {
  return jsonPost<Escenario>("/api/etrm/escenarios", payload);
}

export function actualizarEscenario(id: number, payload: EscenarioInput): Promise<Escenario> {
  return jsonPut<Escenario>(`/api/etrm/escenarios/${id}`, payload);
}

export function borrarEscenario(id: number): Promise<{ id: number; eliminado: boolean }> {
  return jsonDelete(`/api/etrm/escenarios/${id}`);
}

/** Comparte (o cambia el permiso) de un escenario con otro usuario. Solo el dueño. */
export function compartirEscenario(
  id: number,
  email: string,
  permiso: "ver" | "editar",
): Promise<{ id: number; compartidos: EscenarioCompartido[] }> {
  return jsonPut(`/api/etrm/escenarios/${id}/compartir`, { email, permiso });
}

/** Deja de compartir un escenario con un usuario. Solo el dueño. */
export function descompartirEscenario(
  id: number,
  email: string,
): Promise<{ id: number; compartidos: EscenarioCompartido[] }> {
  return jsonDelete(`/api/etrm/escenarios/${id}/compartir/${encodeURIComponent(email)}`);
}

/** Usuarios del ETRM con quienes se puede compartir (excluye al solicitante). */
export function getUsuariosEscenarios(): Promise<{ email: string; rol: string }[]> {
  return jsonFetch<{ email: string; rol: string }[]>("/api/etrm/escenarios/usuarios");
}

/** Descarga el Reporte Comercial (PDF: Pipeline de Compras y Ventas) del escenario. */
export async function descargarReporteEscenario(id: number): Promise<void> {
  const res = await fetchAuth(`/api/etrm/escenarios/${id}/reporte-pdf`, {
    credentials: "include",
  });
  if (!res.ok) {
    let detail = `${res.status} ${res.statusText}`;
    try {
      const j = await res.json();
      if (j?.detail) detail = j.detail;
    } catch {
      /* ignore */
    }
    throw new Error(detail);
  }
  const blob = await res.blob();
  const objectUrl = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = objectUrl;
  const disp = res.headers.get("Content-Disposition") ?? "";
  const m = /filename="([^"]+)"/.exec(disp);
  a.download = m ? m[1] : `Reporte_Comercial_${id}.pdf`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(objectUrl);
}

export function simularEscenario(payload: {
  producto: string;
  agrupacion: string;
  horizonte: number;
  incluir_pasado: boolean;
  energia_agrupada: boolean;
  operaciones: OperacionEscenario[];
}): Promise<SimulacionEscenario> {
  return jsonPost<SimulacionEscenario>("/api/etrm/escenarios/simular", payload);
}

export function getContrato(id: number): Promise<ContratoDetalle> {
  return jsonFetch<ContratoDetalle>(`/api/etrm/contratos/${id}`);
}

export function saveContrato(payload: ContratoInput): Promise<ContratoListItem> {
  return jsonPost<ContratoListItem>("/api/etrm/contratos", payload);
}

export function renombrarContratoFolio(
  idContrato: number,
  folio: string,
): Promise<ContratoListItem> {
  return jsonPut<ContratoListItem>(`/api/etrm/contratos/${idContrato}/folio`, { folio });
}

export function setCorreoContraparte(
  idContrato: number,
  correo_contraparte: string,
): Promise<ContratoListItem> {
  return jsonPut<ContratoListItem>(`/api/etrm/contratos/${idContrato}/correo-contraparte`, {
    correo_contraparte,
  });
}

/** Vigencia comercial del contrato (ampliar/recortar una OC) — no toca los parámetros. */
export function setVigenciaContrato(
  idContrato: number,
  fechas: { fecha_inicio: string; fecha_fin: string },
): Promise<ContratoListItem> {
  return jsonPut<ContratoListItem>(`/api/etrm/contratos/${idContrato}/vigencia`, fechas);
}

export function setProduccionContrato(
  idContrato: number,
  fechas: { produccion_inicio: string | null; produccion_fin: string | null },
): Promise<ContratoListItem> {
  return jsonPut<ContratoListItem>(`/api/etrm/contratos/${idContrato}/produccion`, fechas);
}

export function setTbfinAutomatica(
  idContrato: number,
  tbfinAutomatica: boolean,
): Promise<ContratoListItem> {
  return jsonPut<ContratoListItem>(`/api/etrm/contratos/${idContrato}/tbfin-automatica`, {
    tbfin_automatica: tbfinAutomatica,
  });
}

/** Plazo de pago de la OC (mig 083): con él /facturacion precalcula la fecha
 *  límite desde el último día del periodo facturado. `dias` null = sin plazo. */
export function setPlazoPago(
  idContrato: number,
  plazo: { plazo_pago_dias: number | null; plazo_pago_habiles: boolean },
): Promise<ContratoListItem> {
  return jsonPut<ContratoListItem>(`/api/etrm/contratos/${idContrato}/plazo-pago`, plazo);
}

export function saveParametros(
  idContrato: number,
  payload: ParametroInput,
): Promise<ContratoDetalle> {
  return jsonPost<ContratoDetalle>(
    `/api/etrm/contratos/${idContrato}/parametros`,
    payload,
  );
}

export function deleteParametros(
  idContrato: number,
  idParam: number,
): Promise<ContratoDetalle> {
  return jsonDelete<ContratoDetalle>(
    `/api/etrm/contratos/${idContrato}/parametros/${idParam}`,
  );
}

// ---------- Cálculo TBFin ----------------------------------------------------

export type VariableResuelta = {
  nombre: string;
  fuente: string;
  valor: number;
  detalle: string | null;
};

export type TbfinInsumos = {
  vigente_desde: string;
  formula_strike: string;
  formula_mwh: string;
  variables: VariableResuelta[];
};

export type TbfinHora = {
  hora: number;
  pml: number | null;
  activada: boolean;
  mwh: number;
  total: number;
};

export type TbfinResultado = {
  id_contrato: number;
  folio: string;
  contraparte: string;
  cuenta_orden: string | null;
  rol_mpp: string | null;
  nodo_pml: string;
  fecha_operacion: string;
  insumos: TbfinInsumos;
  strike: number;
  horas: TbfinHora[];
  horas_activas: number;
  mwh_total: number;
  monto_total: number;
  advertencias: string[];
  estado: string;
  confirmado_por: string | null;
  confirmado_en: string | null;
  folio_cenace: string | null;
  estatus_cenace: string | null;
};

// ---- Reporte de activación del strike (TBFin) por rango de fechas ----
export type ActivacionHora = {
  hora: number;
  pml: number | null;
  activada: boolean;
  mwh: number; // 0 si no se activó
  total: number; // PML × MWh (MXN)
};

export type ActivacionDia = {
  fecha: string;
  error?: string; // día que no se pudo calcular (sin parámetros vigentes, etc.)
  strike?: number;
  horas_activas?: number;
  primera_hora?: number | null;
  ultima_hora?: number | null;
  mwh?: number;
  monto_mxn?: number; // pata MEM (PML × MWh), siempre MXN
  pml_max?: number | null;
  horas?: ActivacionHora[];
  fuente?: "snapshot" | "vivo";
  estado?: string;
  advertencias?: string[];
};

export type ActivacionStrike = {
  contrato: string;
  contraparte: string;
  nodo_pml: string;
  modalidad: string;
  take_or_pay: boolean;
  automatica: boolean; // TBFin formalizada sola por CENACE (p. ej. Atria)
  rol_mpp: string;
  desde: string;
  hasta: string;
  dias: ActivacionDia[];
  resumen: {
    dias_totales: number;
    dias_con_activacion: number;
    horas_activadas: number;
    mwh_total: number;
    primera_activacion: { fecha: string; hora: number } | null;
    ultima_activacion: { fecha: string; hora: number } | null;
  };
};

// contrato='*' devuelve un reporte por contrato con motor TBFin.
export type ActivacionRespuesta =
  | (ActivacionStrike & { multiple?: undefined })
  | { multiple: true; desde: string; hasta: string; reportes: ActivacionStrike[] };

export function getActivacionStrike(
  contrato: string,
  desde: string,
  hasta: string,
): Promise<ActivacionRespuesta> {
  const p = new URLSearchParams({ contrato, desde, hasta });
  return jsonFetch<ActivacionRespuesta>(`/api/etrm/tbfin/activacion?${p.toString()}`);
}

/** Descarga el reporte de activación como .xlsx (Resumen + hoja por contrato). */
export async function descargarActivacionStrikeXlsx(
  contrato: string,
  desde: string,
  hasta: string,
): Promise<void> {
  const p = new URLSearchParams({ contrato, desde, hasta });
  const res = await fetchAuth(`/api/etrm/tbfin/activacion/xlsx?${p.toString()}`, {
    credentials: "include",
  });
  if (!res.ok) {
    let detail = `${res.status} ${res.statusText}`;
    try {
      const j = await res.json();
      if (j?.detail) detail = j.detail;
    } catch {
      /* ignore */
    }
    throw new Error(detail);
  }
  const blob = await res.blob();
  const objectUrl = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = objectUrl;
  const disp = res.headers.get("Content-Disposition") ?? "";
  const m = /filename="([^"]+)"/.exec(disp);
  a.download = m ? m[1] : `activacion_strike_${desde}_${hasta}.xlsx`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(objectUrl);
}

export function calcularTbfin(opts: {
  contrato: string;
  fecha: string;
}): Promise<TbfinResultado> {
  const p = new URLSearchParams();
  p.set("contrato", opts.contrato);
  p.set("fecha", opts.fecha);
  return jsonFetch<TbfinResultado>(`/api/etrm/tbfin/calcular?${p.toString()}`);
}

export function confirmarTbfin(contrato: string, fecha: string): Promise<TbfinResultado> {
  return jsonPost<TbfinResultado>("/api/etrm/tbfin/confirmar", { contrato, fecha });
}

export function reabrirTbfin(contrato: string, fecha: string): Promise<TbfinResultado> {
  return jsonPost<TbfinResultado>("/api/etrm/tbfin/reabrir", { contrato, fecha });
}

export function enviarTbfin(contrato: string, fecha: string): Promise<TbfinResultado> {
  return jsonPost<TbfinResultado>("/api/etrm/tbfin/enviar", { contrato, fecha });
}

/** Reabre un envío ('enviado' -> 'confirmado') para poder reenviar. */
export function cancelarEnvioTbfin(contrato: string, fecha: string): Promise<TbfinResultado> {
  return jsonPost<TbfinResultado>("/api/etrm/tbfin/cancelar-envio", { contrato, fecha });
}

/** Lanza el Job que lee Home/Index del SIM y actualiza folio/estatus CENACE (REG/ACE). */
export function syncEstatusCenace(): Promise<{ lanzado: boolean; mensaje: string }> {
  return jsonPost("/api/etrm/tbfin/sync-estatus", {});
}

/** Edita manualmente el folio CENACE de un cálculo (permiso editar_folio). */
export function editarFolioTbfin(
  contrato: string,
  fecha: string,
  folio: string,
): Promise<TbfinResultado> {
  return jsonPut<TbfinResultado>("/api/etrm/tbfin/folio", {
    contrato,
    fecha,
    folio_cenace: folio,
  });
}

export type BitacoraEntrada = {
  evento: string;
  usuario: string | null;
  detalle: string | null;
  ts: string | null;
};

export function getBitacoraTbfin(contrato: string, fecha: string): Promise<BitacoraEntrada[]> {
  const p = new URLSearchParams();
  p.set("contrato", contrato);
  p.set("fecha", fecha);
  return jsonFetch<BitacoraEntrada[]>(`/api/etrm/tbfin/bitacora?${p.toString()}`);
}

/** ENVÍO REAL automatizado: dispara el Cloud Run Job que registra en el SIM. */
export function enviarCenaceTbfin(
  contrato: string,
  fecha: string,
  pruebaDeacero = false,
): Promise<TbfinResultado> {
  const qs = pruebaDeacero ? "?prueba_deacero=true" : "";
  return jsonPost<TbfinResultado>(`/api/etrm/tbfin/enviar-cenace${qs}`, { contrato, fecha });
}

export type PlanEnvio = {
  enviado: boolean;
  modo: string;
  mensaje: string;
  sim_url: string;
  sistema: string;
  participante_rol: string;
  clv_participante: string | null;
  clv_subcuenta: string | null;
  id_cuenta_contraparte: string | null;
  cuenta_orden_contraparte: string | null;
  producto: string;
  mercado: string;
  tipo_precio: string;
  nodo: string;
  fecha_inicio: string;
  fecha_fin: string;
  horas: { hora: number; mwh: number }[];
  archivo_nombre: string;
  archivo_preview: string;
  faltantes: string[];
  conexion: string | null;
};

export function prepararEnvioTbfin(
  contrato: string,
  fecha: string,
  probarConexion = false,
  pruebaDeacero = false,
): Promise<PlanEnvio> {
  const p = new URLSearchParams();
  if (probarConexion) p.set("probar_conexion", "true");
  if (pruebaDeacero) p.set("prueba_deacero", "true");
  const qs = p.toString() ? `?${p.toString()}` : "";
  return jsonPost<PlanEnvio>(`/api/etrm/tbfin/preparar-envio${qs}`, { contrato, fecha });
}

// ---------- Credenciales CENACE (FIEL + SIM) --------------------------------

/** Datos PÚBLICOS del .cer de la e.firma cargado (o el motivo de no poder leerlo). */
export type FielCertificado = {
  titular?: string | null;
  rfc?: string | null;
  curp?: string | null;
  serie?: string | null;
  emisor?: string | null;
  desde?: string;            // YYYY-MM-DD, inicio de vigencia
  vence?: string;            // YYYY-MM-DD, fin de vigencia
  vence_ts?: string;         // ISO con hora, zona MX
  dias_restantes?: number;   // negativo si ya venció
  vencido?: boolean;
  aun_no_vigente?: boolean;
  error?: string;            // el .cer cargado no se pudo leer
};

export type EstadoCredenciales = {
  sim_usuario: boolean;
  sim_clave: boolean;
  fiel_password: boolean;
  fiel_cer: boolean;
  fiel_key: boolean;
  fiel: FielCertificado | null; // null = no hay .cer cargado
};

// ---- Proyecciones de ingresos/egresos B2B (calendario de facturación) ----
export type Proyeccion = {
  id: number;
  clave: string | null; // ID del Excel (CS-006)
  contraparte: string;
  oc: string | null;
  id_contrato: number | null; // contrato mapeado (null = sin mapear)
  folio: string | null;
  producto: string;
  tipo: "ingreso" | "egreso";
  subtotal: number;
  total: number | null;
  moneda: "MXN" | "USD";
  fecha_facturacion: string;
  cargado_por: string | null;
  cargado_en: string;
};

export function getProyecciones(): Promise<{ items: Proyeccion[] }> {
  return jsonFetch<{ items: Proyeccion[] }>("/api/etrm/proyecciones");
}

/** Sube el Excel de proyecciones; REEMPLAZA el juego completo. */
export async function cargarProyecciones(
  fd: FormData,
): Promise<{ cargadas: number; mapeadas: number; sin_mapear: string[] }> {
  const res = await fetchAuth("/api/etrm/proyecciones/cargar", {
    method: "POST",
    credentials: "include",
    body: fd,
  });
  if (!res.ok) {
    let detail = `${res.status} ${res.statusText}`;
    try {
      const j = await res.json();
      if (j?.detail) detail = j.detail;
    } catch {
      /* ignore */
    }
    throw new Error(detail);
  }
  return res.json();
}

export function borrarProyeccion(id: number): Promise<{ id: number; eliminada: boolean }> {
  return jsonDelete(`/api/etrm/proyecciones/${id}`);
}

export function vaciarProyecciones(): Promise<{ eliminadas: number }> {
  return jsonDelete("/api/etrm/proyecciones");
}

// ---------- Importaciones y exportaciones (energía asignada CENACE) ---------

export type IntercambioCarga = {
  id_carga: number;
  fecha: string;
  participante: string;
  nombre_participante: string | null;
  archivo: string | null;
  unidades: number;
  horas: number;
  importado: number;
  exportado: number;
  es_mpp: boolean;
  cargado_por: string | null;
  cargado_en: string;
};

export type IntercambioResumen = {
  rango: {
    desde: string | null;
    hasta: string | null;
    cargado_desde: string | null;
    cargado_hasta: string | null;
  };
  participante_mpp: string;
  participantes: {
    participante: string;
    nombre: string | null;
    importado: number;
    exportado: number;
    es_mpp: boolean;
  }[];
  puntos: {
    punto: string;
    region: string | null;
    nombre: string;
    importado: number;
    exportado: number;
  }[];
  /** Una fila por (día × punto) — el frontend la pivotea para la gráfica. */
  dias: { fecha: string; punto: string; importado: number; exportado: number }[];
  horas: { hora: number; importado: number; exportado: number }[];
  totales: { importado: number; exportado: number; neto: number; dias: number };
};

export type IntercambioDia = {
  fecha: string;
  participante: string;
  nombre: string | null;
  archivo: string | null;
  horas: number;
  cargado_por: string | null;
  cargado_en: string;
  filas: {
    sistema: string;
    clave: string;
    tipo: "IMP" | "EXP";
    punto: string;
    region: string | null;
    nombre_punto: string;
    mwh: number[];
    total: number;
  }[];
};

export type IntercambioCargaResultado = {
  archivo: string;
  ok: boolean;
  error?: string;
  fecha?: string;
  participante?: string;
  nombre?: string | null;
  unidades?: number;
  importado?: number;
  exportado?: number;
  avisos?: string[];
};

function paramsIntercambios(
  desde: string | null,
  hasta: string | null,
  participantes: string[],
  puntos: string[],
): URLSearchParams {
  const p = new URLSearchParams();
  if (desde) p.set("desde", desde);
  if (hasta) p.set("hasta", hasta);
  if (participantes.length) p.set("participantes", participantes.join(","));
  if (puntos.length) p.set("puntos", puntos.join(","));
  return p;
}

export function getIntercambios(
  desde: string | null,
  hasta: string | null,
  participantes: string[] = [],
  puntos: string[] = [],
): Promise<IntercambioResumen> {
  const p = paramsIntercambios(desde, hasta, participantes, puntos);
  return jsonFetch<IntercambioResumen>(`/api/etrm/intercambios?${p.toString()}`);
}

export function getIntercambioDia(
  fecha: string,
  participante: string,
): Promise<IntercambioDia> {
  const p = new URLSearchParams({ fecha, participante });
  return jsonFetch<IntercambioDia>(`/api/etrm/intercambios/dia?${p.toString()}`);
}

export function getIntercambioCargas(): Promise<{ items: IntercambioCarga[] }> {
  return jsonFetch<{ items: IntercambioCarga[] }>("/api/etrm/intercambios/cargas");
}

/** Sube uno o VARIOS .xlsx de energía asignada; cada día reemplaza al anterior. */
export async function cargarIntercambios(
  fd: FormData,
): Promise<{ cargados: number; fallidos: number; resultados: IntercambioCargaResultado[] }> {
  const res = await fetchAuth("/api/etrm/intercambios/cargar", {
    method: "POST",
    credentials: "include",
    body: fd,
  });
  if (!res.ok) throw new Error(await detalleDe(res));
  return res.json();
}

export function borrarIntercambioCarga(
  idCarga: number,
): Promise<{ id_carga: number; eliminada: boolean }> {
  return jsonDelete(`/api/etrm/intercambios/cargas/${idCarga}`);
}

export async function descargarIntercambiosXlsx(
  desde: string | null,
  hasta: string | null,
  participantes: string[] = [],
  puntos: string[] = [],
): Promise<void> {
  const p = paramsIntercambios(desde, hasta, participantes, puntos);
  const res = await fetchAuth(`/api/etrm/intercambios/xlsx?${p.toString()}`, {
    credentials: "include",
  });
  if (!res.ok) throw new Error(await detalleDe(res));
  const blob = await res.blob();
  const objectUrl = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = objectUrl;
  const disp = res.headers.get("Content-Disposition") ?? "";
  const m = /filename="([^"]+)"/.exec(disp);
  a.download = m ? m[1] : `intercambios_${desde ?? ""}_${hasta ?? ""}.xlsx`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(objectUrl);
}

// ---------- Registro Automatizado (SAT) ------------------------------------

export type RegAutoAutorizacion = {
  id_autorizacion: number;
  cruce: string;
  punto: string | null;
  razon_social: string | null;
  autorizacion: string | null;
  activo: boolean;
  operaciones: number;
};

export type RegAutoColumna = { num: string; texto: string };

export type RegAutoOperacion = {
  id_operacion: number;
  pedimento_numero: string | null;
  pedimento_fecha: string | null;
  pedimento_clave: string | null;
  id_factura: number | null;
  cfdi_folio: string | null;
  cfdi_valor: number | null;
  cfdi_cantidad: number | null;
  medidor_lectura: number | null;
  medidor_fecha: string | null;
  notas: string | null;
  capturado_por: string | null;
  capturado_en: string;
  factura_uuid: string | null;
  emisor_nombre: string | null;
  mes: string | null;
  /** (Σ cantidad CFDI − Σ lectura) ÷ Σ lectura del mes; lo calcula el backend. */
  pct_mensual: number | null;
  pct_anual: number | null;
};

export type RegAutoRegistro = {
  autorizacion: {
    id_autorizacion: number;
    cruce: string;
    punto: string | null;
    razon_social: string | null;
    autorizacion: string | null;
  };
  ejercicio: number;
  encabezado: string;
  columnas: RegAutoColumna[];
  items: RegAutoOperacion[];
  sin_porcentaje: number[];
};

export type RegAutoSugerencia = {
  id_factura: number;
  uuid: string | null;
  serie: string | null;
  folio: string | null;
  numero: string | null;
  fecha: string | null;
  total: number | null;
  moneda: string | null;
  emisor_rfc: string | null;
  emisor_nombre: string | null;
  cantidad: number;
  unidad: string;
};

export type RegAutoOperacionIn = {
  id_autorizacion: number;
  ejercicio: number;
  pedimento_numero?: string | null;
  pedimento_fecha?: string | null;
  pedimento_clave?: string | null;
  id_factura?: number | null;
  cfdi_folio?: string | null;
  cfdi_valor?: number | null;
  cfdi_cantidad?: number | null;
  medidor_lectura?: number | null;
  medidor_fecha?: string | null;
  notas?: string | null;
};

export function getRegAutoAutorizaciones(): Promise<{
  items: RegAutoAutorizacion[];
  columnas: RegAutoColumna[];
}> {
  return jsonFetch("/api/etrm/registro-automatizado/autorizaciones");
}

export function guardarRegAutoAutorizacion(
  id: number,
  payload: { razon_social?: string | null; autorizacion?: string | null },
): Promise<RegAutoAutorizacion> {
  return jsonPut(`/api/etrm/registro-automatizado/autorizaciones/${id}`, payload);
}

export function getRegAuto(
  idAutorizacion: number,
  ejercicio: number,
): Promise<RegAutoRegistro> {
  const p = new URLSearchParams({
    id_autorizacion: String(idAutorizacion), ejercicio: String(ejercicio),
  });
  return jsonFetch(`/api/etrm/registro-automatizado?${p.toString()}`);
}

export function getRegAutoSugerencias(
  ejercicio: number,
): Promise<{ items: RegAutoSugerencia[] }> {
  return jsonFetch(`/api/etrm/registro-automatizado/sugerencias?ejercicio=${ejercicio}`);
}

export function crearRegAutoOperacion(
  payload: RegAutoOperacionIn,
): Promise<{ id_operacion: number }> {
  return jsonPost("/api/etrm/registro-automatizado", payload);
}

export function editarRegAutoOperacion(
  id: number,
  payload: RegAutoOperacionIn,
): Promise<{ id_operacion: number }> {
  return jsonPut(`/api/etrm/registro-automatizado/${id}`, payload);
}

export function borrarRegAutoOperacion(
  id: number,
): Promise<{ id_operacion: number; eliminado: boolean }> {
  return jsonDelete(`/api/etrm/registro-automatizado/${id}`);
}

export async function descargarRegAutoPdf(
  idAutorizacion: number,
  ejercicio: number,
): Promise<void> {
  const p = new URLSearchParams({
    id_autorizacion: String(idAutorizacion), ejercicio: String(ejercicio),
  });
  const res = await fetchAuth(`/api/etrm/registro-automatizado/pdf?${p.toString()}`, {
    credentials: "include",
  });
  if (!res.ok) throw new Error(await detalleDe(res));
  const blob = await res.blob();
  const objectUrl = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = objectUrl;
  const disp = res.headers.get("Content-Disposition") ?? "";
  const m = /filename="([^"]+)"/.exec(disp);
  a.download = m ? m[1] : `Registro Automatizado ${ejercicio}.pdf`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(objectUrl);
}

export function getEstadoCredenciales(): Promise<EstadoCredenciales> {
  return jsonFetch<EstadoCredenciales>("/api/etrm/cenace/credenciales");
}

export async function guardarCredenciales(
  fd: FormData,
): Promise<{ guardados: string[]; estado: EstadoCredenciales }> {
  const res = await fetchAuth("/api/etrm/cenace/credenciales", {
    method: "POST",
    credentials: "include",
    body: fd,
  });
  if (!res.ok) {
    let detail = `${res.status} ${res.statusText}`;
    try {
      const j = await res.json();
      if (j?.detail) detail = j.detail;
    } catch {
      /* ignore */
    }
    throw new Error(detail);
  }
  return res.json();
}

/** Descarga el archivo CENACE (CSV) del cálculo confirmado/enviado. */
export async function descargarArchivoTbfin(contrato: string, fecha: string): Promise<void> {
  const res = await fetchAuth(
    `/api/etrm/tbfin/archivo?contrato=${encodeURIComponent(contrato)}&fecha=${fecha}`,
    { credentials: "include" },
  );
  if (!res.ok) {
    let detail = `${res.status} ${res.statusText}`;
    try {
      const j = await res.json();
      if (j?.detail) detail = j.detail;
    } catch {
      /* ignore */
    }
    throw new Error(detail);
  }
  const blob = await res.blob();
  const objectUrl = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = objectUrl;
  const disp = res.headers.get("Content-Disposition") ?? "";
  const m = /filename="([^"]+)"/.exec(disp);
  a.download = m ? m[1] : `tbfin_${fecha}.csv`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(objectUrl);
}

// ---------- Matriz mensual de estado ----------------------------------------

export type MatrizContrato = {
  id_contrato: number;
  folio: string;
  celdas: Record<string, string>; // "dia" -> 'pendiente'|'confirmado'|'enviado'
};

export type MatrizResponse = {
  anio: number;
  mes: number;
  dias: number;
  modo: string;
  contratos: MatrizContrato[];
};

export function getMatrizTbfin(opts: {
  anio: number;
  mes: number;
  modo?: string;
}): Promise<MatrizResponse> {
  const p = new URLSearchParams();
  p.set("anio", String(opts.anio));
  p.set("mes", String(opts.mes));
  if (opts.modo) p.set("modo", opts.modo);
  return jsonFetch<MatrizResponse>(`/api/etrm/tbfin/matriz?${p.toString()}`);
}

// ---------- Cálculo de facturación (liquidación quincenal) -------------------

export type FacturacionDia = {
  fecha: string;
  horas_activas: number;
  strike: number;
  ceu_d: number;
  treg: number;
  tc_d: number;
  ee: number;
  ce_dia: number;
  ctreg_dia: number;
  mwh: number;
  pe_usd: number | null;
  tc_cf: number | null;
};

export type FacturacionResultado = {
  id_contrato: number;
  folio: string;
  contraparte: string;
  rol_mpp: string | null;
  moneda: string;
  desde: string;
  hasta: string;
  dias: number;
  ht_q: number;
  sum_z: number;
  tc_q: number;
  pf: number;
  pf_tbfin: number;
  iva_pct: number;
  ce_q: number;
  ctreg_q: number;
  cf_q: number;
  subtotal: number;
  iva: number;
  total: number;
  mwh_total: number;
  dias_detalle: FacturacionDia[];
  advertencias: string[];
  /** Insumos usados en el periodo (hr, cvom, hsc, cpi, treg, tc…).
   *  Vacío en facturas/sombras guardadas antes de que se agregara. */
  insumos_q?: InsumoPeriodo[];
  /** Cargos/créditos manuales (subtotal/iva/total YA los incluyen). */
  manuales?: LineaManual[];
  /** Fecha límite de pago (mig 083): precalculada con el plazo de la OC desde
   *  el último día del periodo facturado; el usuario la puede cambiar. */
  fecha_limite_pago?: string | null;
  plazo_pago_dias?: number | null;
  plazo_pago_habiles?: boolean;
};

export type InsumoPeriodo = {
  nombre: string;
  minimo: number;
  maximo: number;
  promedio: number;
  constante: boolean;
};

/** Factura de POTENCIA de un mes: la parcialidad del cargo por MW-año. */
export type PotenciaFactura = {
  id_contrato: number;
  folio: string;
  contraparte: string;
  moneda: string;
  ym: string;
  n_pago: number;
  num_parcialidades: number;
  pc_mw_ano: number;
  cantidad: number;
  precio_unitario: number;
  importe: number;
  iva_pct: number;
  iva: number;
  total: number;
  descripcion: string;
  tc_informativo: number | null;
  tc_fecha: string | null;
  clave_prod_serv: string;
  clave_unidad: string;
  acumulado_mw: number;
  acumulado_importe: number;
  advertencias: string[];
  /** Factura ya guardada de esta parcialidad: no se vuelve a emitir (mig 085). */
  factura_id?: number | null;
  /** Fecha límite de pago (mig 083): precalculada con el plazo de la OC desde
   *  el último día del periodo facturado; el usuario la puede cambiar. */
  fecha_limite_pago?: string | null;
  plazo_pago_dias?: number | null;
  plazo_pago_habiles?: boolean;

  /** Cargos/créditos manuales capturados al guardar (iva/total YA los incluyen). */
  manuales?: LineaManual[];
};

/** Se factura por parcialidad (calendario de la OC) o por mes (pagos regulares). */
export function facturarPotencia(
  contrato: string,
  sel: { ym?: string; n_pago?: number },
): Promise<PotenciaFactura> {
  const q = sel.n_pago != null ? `n_pago=${sel.n_pago}` : `ym=${sel.ym}`;
  return jsonFetch<PotenciaFactura>(
    `/api/etrm/facturacion/potencia?contrato=${encodeURIComponent(contrato)}&${q}`,
  );
}

/** Una parcialidad del calendario de pagos de una venta de potencia. */
export type PotenciaParcialidad = {
  n: number;
  ym: string;
  tipo: "fijo" | "residual" | "ajuste";
  monto: number;
  cantidad: number;
  precio_unitario: number;
  facturada: boolean;
  /** Factura guardada en el ETRM de esta parcialidad (mig 085). */
  factura_id?: number | null;
  cfdi_folio: string | null;
  cfdi_fecha: string | null;
  cfdi_importe: number | null;
  nota: string | null;
};

/** Plan de pagos: `modo` decide si se elige parcialidad o Año-Mes. */
export type PotenciaCalendario = {
  id_contrato: number;
  folio: string;
  contraparte: string;
  moneda: string;
  modo: "explicito" | "periodo";
  pc_mw_ano: number;
  precio_unitario: number;
  cargo_total: number;
  iva_pct: number;
  num_parcialidades: number;
  pagos_por_ano: number | null;
  primer_pago_ym: string | null;
  ultimo_pago_ym: string | null;
  pnp_mw_ano: number | null;
  pnp_anio_produccion: number | null;
  pnp_version: string | null;
  factor_pnp: number | null;
  parcialidades: PotenciaParcialidad[];
  facturado: number;
  pendiente: number;
  advertencias: string[];
};

export function calendarioPotencia(contrato: string): Promise<PotenciaCalendario> {
  return jsonFetch<PotenciaCalendario>(
    `/api/etrm/facturacion/potencia/calendario?contrato=${encodeURIComponent(contrato)}`,
  );
}

/** Factura de certificados (CEL o i-REC): los de la OC a su precio unitario. */
export type CelFactura = {
  /** Factura ya guardada de esta OC (o de esta entrega): no se vuelve a emitir. */
  factura_id?: number | null;
  /** OC con calendario de entregas (cal_* en la ficha): cuál se factura (0 = OC completa). */
  n_pago?: number;
  entregas?: { n: number; ym: string; cel: number; importe: number; factura_id: number | null }[];
  /** Fecha límite de pago (mig 083): precalculada con el plazo de la OC desde
   *  el último día del periodo facturado; el usuario la puede cambiar. */
  fecha_limite_pago?: string | null;
  plazo_pago_dias?: number | null;
  plazo_pago_habiles?: boolean;

  id_contrato: number;
  folio: string;
  /** "CEL" | "i-REC" — para etiquetar la cantidad en la UI */
  producto: string;
  contraparte: string;
  moneda: string;
  ym: string;
  cel: number;
  precio_unitario: number;
  importe: number;
  iva_pct: number;
  iva: number;
  total: number;
  descripcion: string;
  tc_informativo: number | null;
  tc_fecha: string | null;
  clave_prod_serv: string;
  clave_unidad: string;
  vigencia_desde: string | null;
  vigencia_hasta: string | null;
  advertencias: string[];
  /** Cargos/créditos manuales capturados al guardar (iva/total YA los incluyen). */
  manuales?: LineaManual[];
};

export function facturarCel(contrato: string, ym: string): Promise<CelFactura> {
  return jsonFetch<CelFactura>(
    `/api/etrm/facturacion/cel?contrato=${encodeURIComponent(contrato)}&ym=${ym}`,
  );
}

/** Factura de i-REC: mismo motor y forma que los CEL. */
export function facturarIrec(contrato: string, ym: string): Promise<CelFactura> {
  return jsonFetch<CelFactura>(
    `/api/etrm/facturacion/irec?contrato=${encodeURIComponent(contrato)}&ym=${ym}`,
  );
}

/** Factura PRELIMINAR (prepago §20) o AJUSTE por gas (§18) — contratos prepago_gas. */
export type PreliminarFactura = {
  folio: string;
  contraparte: string;
  rol_mpp: string;
  modo: "preliminar" | "ajuste";
  ym: string;
  dias: number;
  horas: number;
  ee: number;
  mwh_total: number;
  gas_promedio: number; // calculado (prellenado, editable)
  gas_usado: number; // el que entró al cálculo
  gas_detalle: string;
  puc: number;
  hr: number;
  cvom: number;
  adder: number;
  treg_mxn_mwh: number;
  tc: number;
  tc_fecha: string | null;
  ce_m: number;
  ctreg_m: number;
  subtotal: number;
  iva_pct: number;
  iva: number;
  total: number;
  moneda: string;
  /** El promedio entra al cálculo completo; `gas_usado` es lo que se muestra. */
  gas_usado_exacto?: number | null;
  gas_preliminar?: number | null;
  gas_preliminar_exacto?: number | null;
  ce_preliminar?: number | null;
  /** CTreg y TC con que se emitió la preliminar: el ajuste revalorúa el mismo
   *  cargo regulado (MXN/MWh) al TC del cierre. */
  ctreg_preliminar?: number | null;
  tc_preliminar?: number | null;
  dif_ce?: number | null;
  dif_ctreg?: number | null;
  dif_subtotal?: number | null;
  dif_iva?: number | null;
  dif_total?: number | null;
  tipo_ajuste?: string | null;
  // Meses del FLUJO en /b2b/utilidad (solo facturas guardadas): el de esta
  // factura y, en los ajustes, el de la preliminar que reparte + su subtotal.
  mes_flujo?: string | null;
  mes_flujo_preliminar?: string | null;
  subtotal_preliminar?: number | null;
  advertencias: string[];
  /** Prepago_gas: vencimiento de la factura PRELIMINAR (mig 084). El de abajo
   *  es el del documento vigente (el ajuste, cuando el mes ya se ajustó). */
  fecha_limite_pago_preliminar?: string | null;
  /** Fecha límite de pago (mig 083): precalculada con el plazo de la OC desde
   *  el último día del periodo facturado; el usuario la puede cambiar. */
  fecha_limite_pago?: string | null;
  plazo_pago_dias?: number | null;
  plazo_pago_habiles?: boolean;
};

export function facturarPreliminar(
  contrato: string,
  ym: string,
  modo: "preliminar" | "ajuste",
  gas?: number | null,
  gasPreliminar?: number | null,
  tcFecha?: string | null, // fecha del FIX a usar como TC (vacío = regla automática)
): Promise<PreliminarFactura> {
  return jsonFetch<PreliminarFactura>(
    `/api/etrm/facturacion/preliminar?contrato=${encodeURIComponent(contrato)}&ym=${ym}&modo=${modo}` +
      (gas != null ? `&gas=${gas}` : "") +
      (gasPreliminar != null ? `&gas_preliminar=${gasPreliminar}` : "") +
      (tcFecha ? `&tc_fecha=${tcFecha}` : ""),
  );
}

/** Persiste la preliminar como factura del mes; el ajuste actualiza esa misma
 *  factura al valor final y guarda la diferencia en la memoria. */
export function guardarFacturaPreliminar(
  contrato: string,
  ym: string,
  modo: "preliminar" | "ajuste",
  gas?: number | null,
  gasPreliminar?: number | null,
  mesFlujo?: string | null, // mes donde cae el DINERO en /b2b/utilidad
  tcFecha?: string | null, // fecha del FIX a usar como TC (vacío = regla automática)
  manuales: LineaManual[] = [],
  fechaLimite?: string | null, // vacía = la que precalcula el plazo de la OC
  fechaLimitePreliminar?: string | null, // solo ajustes: vencimiento de la preliminar
): Promise<FacturaItem> {
  return jsonPost<FacturaItem>(
    `/api/etrm/facturacion/preliminar/guardar?contrato=${encodeURIComponent(contrato)}&ym=${ym}&modo=${modo}` +
      (gas != null ? `&gas=${gas}` : "") +
      (gasPreliminar != null ? `&gas_preliminar=${gasPreliminar}` : "") +
      (mesFlujo ? `&mes_flujo=${mesFlujo}` : "") +
      (tcFecha ? `&tc_fecha=${tcFecha}` : "") +
      (fechaLimite ? `&fecha_limite=${fechaLimite}` : "") +
      (fechaLimitePreliminar ? `&fecha_limite_preliminar=${fechaLimitePreliminar}` : ""),
    { manuales },
  );
}

export function calcularFacturacion(
  contrato: string,
  desde: string,
  hasta: string,
): Promise<FacturacionResultado> {
  const p = new URLSearchParams({ contrato, desde, hasta });
  return jsonFetch<FacturacionResultado>(`/api/etrm/facturacion/calcular?${p.toString()}`);
}

/** Ejercicio Sombra: modelo de lo que la contraparte cobra (contratos comprador). */
export function calcularSombra(
  contrato: string,
  desde: string,
  hasta: string,
): Promise<FacturacionResultado> {
  const p = new URLSearchParams({ contrato, desde, hasta });
  return jsonFetch<FacturacionResultado>(`/api/etrm/facturacion/sombra?${p.toString()}`);
}

/** Modelo de lo que la contraparte debe cobrarnos por una parcialidad de potencia. */
export function sombraPotencia(
  contrato: string,
  sel: { ym?: string; n_pago?: number },
): Promise<PotenciaFactura> {
  const q = sel.n_pago != null ? `n_pago=${sel.n_pago}` : `ym=${sel.ym}`;
  return jsonFetch<PotenciaFactura>(
    `/api/etrm/facturacion/sombra/potencia?contrato=${encodeURIComponent(contrato)}&${q}`,
  );
}

/** Modelo de lo que la contraparte debe cobrarnos por los CEL de la OC. */
export function sombraCel(contrato: string, ym: string): Promise<CelFactura> {
  return jsonFetch<CelFactura>(
    `/api/etrm/facturacion/sombra/cel?contrato=${encodeURIComponent(contrato)}&ym=${ym}`,
  );
}

/** Modelo sombra de i-REC (compras): mismo motor que los CEL. */
export function sombraIrec(contrato: string, ym: string): Promise<CelFactura> {
  return jsonFetch<CelFactura>(
    `/api/etrm/facturacion/sombra/irec?contrato=${encodeURIComponent(contrato)}&ym=${ym}`,
  );
}

export type EjercicioSombraItem = {
  id: number;
  id_contrato: number;
  folio_contrato: string;
  contraparte: string;
  /** "Energía" | "Potencia" | "CEL" — en pot/CEL el concepto único va en ce_q */
  producto: string;
  desde: string;
  hasta: string;
  ce_q: number;
  ctreg_q: number;
  cf_q: number;
  subtotal: number;
  iva: number;
  total: number;
  fac_ce_q: number;
  fac_ctreg_q: number;
  fac_cf_q: number;
  fac_subtotal: number;
  fac_iva: number;
  fac_total: number;
  dif_total: number;
  moneda: string;
  guardado_por: string | null;
  guardado_en: string;
};

/** Energía: desde/hasta + los 3 cargos. Potencia/CEL: ym (+n_pago) y fac_importe. */
export function guardarSombra(payload: {
  contrato: string;
  producto?: string;
  desde?: string;
  hasta?: string;
  fac_ce_q?: number;
  fac_ctreg_q?: number;
  fac_cf_q?: number;
  ym?: string;
  n_pago?: number;
  fac_importe?: number;
}): Promise<EjercicioSombraItem> {
  return jsonPost<EjercicioSombraItem>("/api/etrm/facturacion/sombra/guardar", payload);
}

export function getSombras(): Promise<EjercicioSombraItem[]> {
  return jsonFetch<EjercicioSombraItem[]>("/api/etrm/facturacion/sombra/lista");
}

/** Solo viene poblado el `resultado*` que corresponde al `producto`. */
export type EjercicioSombraDetalle = EjercicioSombraItem & {
  resultado: FacturacionResultado | null;
  resultado_potencia: PotenciaFactura | null;
  resultado_cel: CelFactura | null;
};

export function getSombra(id: number): Promise<EjercicioSombraDetalle> {
  return jsonFetch<EjercicioSombraDetalle>(`/api/etrm/facturacion/sombra/${id}`);
}

/** Borra un ejercicio sombra guardado (permiso 'borrar_sombra'). */
export function borrarSombra(id: number): Promise<{ id: number; eliminado: boolean }> {
  return jsonDelete(`/api/etrm/facturacion/sombra/${id}`);
}

export type FacturaItem = {
  id: number;
  id_contrato: number;
  folio_contrato: string;
  contraparte: string;
  desde: string;
  hasta: string;
  dias: number;
  sum_z: number;
  ce_q: number;
  ctreg_q: number;
  cf_q: number;
  subtotal: number;
  iva: number;
  total: number;
  mwh_total: number;
  moneda: string;
  enviada_flamma: boolean;
  guardado_por: string | null;
  guardado_en: string;
  // 'preliminar' | 'ajuste' (esquema prepago_gas) | 'completa' (motor normal)
  tipo?: string;
  gas_usado?: number | null; // gas de la preliminar/ajuste (prellena el ajuste)
  producto?: string; // 'Energía' | 'Potencia' (mig 077)
  fecha_limite_pago?: string | null; // mig 083
  fecha_limite_pago_preliminar?: string | null; // mig 084 (prepago_gas)
  /** Parcialidad del calendario de potencia; 0 = no aplica (mig 085). Dos
   *  parcialidades pueden caer en el MISMO mes (001MPP/RIC, mayo-2026). */
  n_pago?: number;
};

export type HistoricoMes = { mes: string; mwh: number; monto: number; dias: number };

/** Cargo (+) o crédito (−) manual: entra al subtotal ANTES de IVA al guardar. */
export type LineaManual = { concepto: string; monto: number };

export function guardarFactura(
  contrato: string,
  desde: string,
  hasta: string,
  manuales: LineaManual[] = [],
  fechaLimite?: string | null, // vacía = la que precalcula el plazo de la OC
): Promise<FacturaItem> {
  return jsonPost<FacturaItem>("/api/etrm/facturacion/guardar", {
    contrato, desde, hasta, manuales, fecha_limite_pago: fechaLimite || null,
  });
}

export function getFacturas(contrato: string): Promise<FacturaItem[]> {
  return jsonFetch<FacturaItem[]>(
    `/api/etrm/facturacion/lista?contrato=${encodeURIComponent(contrato)}`,
  );
}

export function getFacturasTodas(): Promise<FacturaItem[]> {
  return jsonFetch<FacturaItem[]>("/api/etrm/facturacion/lista");
}

export type FacturaDetalle = {
  id: number;
  enviada_flamma: boolean;
  guardado_por: string | null;
  guardado_en: string;
  resultado: FacturacionResultado;
  /** Fecha límite de pago guardada (mig 083), editable desde el detalle. */
  fecha_limite_pago?: string | null;
  /** Prepago_gas: vencimiento de la preliminar (mig 084). */
  fecha_limite_pago_preliminar?: string | null;
  // Solo preliminar/ajuste (prepago_gas): mes del dinero en /b2b/utilidad
  tipo?: string;
  mes_flujo?: string | null;
  mes_flujo_preliminar?: string | null;
  // Facturas de POTENCIA: la memoria original de la parcialidad (vista CFDI);
  // `resultado` trae la adaptación de un solo cargo.
  producto?: string;
  resultado_potencia?: PotenciaFactura | null;
  // Facturas de CEL / i-REC: la memoria original (misma vista CFDI)
  resultado_cel?: CelFactura | null;
  // Preliminar/ajuste: memoria original completa (desglose en el detalle)
  preliminar?: PreliminarFactura | null;
};

/** Guarda la factura de una PARCIALIDAD de potencia (upsert por mes). */
export function guardarFacturaPotencia(
  contrato: string,
  ym?: string | null,
  nPago?: number | null,
  manuales: LineaManual[] = [],
  fechaLimite?: string | null, // vacía = la que precalcula el plazo de la OC
): Promise<FacturaItem> {
  const p = new URLSearchParams({ contrato });
  if (ym) p.set("ym", ym);
  if (nPago != null) p.set("n_pago", String(nPago));
  if (fechaLimite) p.set("fecha_limite", fechaLimite);
  return jsonPost<FacturaItem>(
    `/api/etrm/facturacion/potencia/guardar?${p.toString()}`,
    { manuales },
  );
}

/** Guarda la factura de CERTIFICADOS (CEL o i-REC): una por Orden de Compra. */
export function guardarFacturaCel(
  contrato: string,
  ym: string,
  producto: "CEL" | "i-REC" = "CEL",
  manuales: LineaManual[] = [],
  fechaLimite?: string | null, // vacía = la que precalcula el plazo de la OC
): Promise<FacturaItem> {
  const p = new URLSearchParams({ contrato, ym, producto });
  if (fechaLimite) p.set("fecha_limite", fechaLimite);
  return jsonPost<FacturaItem>(
    `/api/etrm/facturacion/cel/guardar?${p.toString()}`,
    { manuales },
  );
}

export function getFactura(id: number): Promise<FacturaDetalle> {
  return jsonFetch<FacturaDetalle>(`/api/etrm/facturacion/factura/${id}`);
}

/** Cambia el mes de flujo B2B de una factura preliminar/ajuste guardada. */
export function actualizarMesFlujo(
  id: number,
  mesFlujo: string,
  mesFlujoPreliminar?: string | null,
): Promise<FacturaDetalle> {
  return jsonFetch<FacturaDetalle>(
    `/api/etrm/facturacion/factura/${id}/mes_flujo?mes_flujo=${mesFlujo}` +
      (mesFlujoPreliminar ? `&mes_flujo_preliminar=${mesFlujoPreliminar}` : ""),
    { method: "PATCH" },
  );
}

/** Corrige la fecha límite de pago de una factura guardada (vacía = sin fecha). */
export function actualizarFechaLimite(
  id: number,
  fechaLimite: string | null,
  // prepago_gas: vencimiento de la preliminar. `undefined` = no se toca.
  fechaLimitePreliminar?: string | null,
): Promise<FacturaDetalle> {
  return jsonFetch<FacturaDetalle>(
    `/api/etrm/facturacion/factura/${id}/fecha_limite`,
    {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        fecha_limite_pago: fechaLimite,
        ...(fechaLimitePreliminar === undefined
          ? {}
          : fechaLimitePreliminar === null
            ? { borrar_preliminar: true }
            : { fecha_limite_pago_preliminar: fechaLimitePreliminar }),
      }),
    },
  );
}

/** Borra una factura guardada (permiso 'borrar_factura'; falla si ya se envió a Flamma). */
export function borrarFactura(id: number): Promise<{ id: number; eliminada: boolean }> {
  return jsonDelete(`/api/etrm/facturacion/factura/${id}`);
}

/** Descarga el PDF de una factura GUARDADA (por id), con auth. */
export async function descargarFacturaPdfById(id: number): Promise<void> {
  const res = await fetchAuth(`/api/etrm/facturacion/factura/${id}/pdf`, {
    credentials: "include",
  });
  if (!res.ok) {
    let detail = `${res.status} ${res.statusText}`;
    try {
      const j = await res.json();
      if (j?.detail) detail = j.detail;
    } catch {
      /* ignore */
    }
    throw new Error(detail);
  }
  const blob = await res.blob();
  const objectUrl = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = objectUrl;
  const disp = res.headers.get("Content-Disposition") ?? "";
  const m = /filename="([^"]+)"/.exec(disp);
  a.download = m ? m[1] : `factura_${id}.pdf`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(objectUrl);
}

// ---------- Facturas de Compra CENACE (public.facturas_compra*) --------------

export type FacturaCompraItem = {
  fuf: string;
  uuid: string | null;
  serie: string | null;
  folio: string | null;
  fecha: string | null;
  periodo_ecd: string | null;
  fecha_lim_pago: string | null;
  sistema: string | null;
  tipo_doc: string | null;
  estatus: string | null;
  moneda: string | null;
  subtotal: number | null;
  total_impuestos_trasladados: number | null;
  total: number | null;
  emisor_rfc: string | null;
  emisor_nombre: string | null;
  receptor_rfc: string | null;
  receptor_nombre: string | null;
  nombre_archivo: string | null;
  fecha_publicacion: string | null;
  id_partic_cenace: string | null;
  banco: string | null;
  cuenta: string | null;
  clabe: string | null;
  ref_banco: string | null;
  cuenta_orden: string | null;
  monto_letra: string | null;
  tiene_pdf: boolean;
  tiene_xml: boolean;
};

export type FacturaCompraConcepto = {
  num_lin: number;
  folio_unico: string | null;
  concepto: string | null;
  cantidad: number | null;
  unidad: string | null;
  precio_unit: number | null;
  importe_linea: number | null;
  iva: number | null;
  total: number | null;
};

/** Lista de facturas de compra CENACE filtrada por periodo_ecd. */
export function getFacturasCompra(
  desde?: string,
  hasta?: string,
): Promise<{ items: FacturaCompraItem[] }> {
  const p = new URLSearchParams();
  if (desde) p.set("desde", desde);
  if (hasta) p.set("hasta", hasta);
  const qs = p.toString() ? `?${p.toString()}` : "";
  return jsonFetch<{ items: FacturaCompraItem[] }>(`/api/etrm/facturas-compra${qs}`);
}

export function getFacturaCompraConceptos(fuf: string): Promise<FacturaCompraConcepto[]> {
  return jsonFetch<FacturaCompraConcepto[]>(
    `/api/etrm/facturas-compra/${encodeURIComponent(fuf)}/conceptos`,
  );
}

/** Descarga el PDF o XML de una factura de compra (viene de GCS, con auth). */
export async function descargarFacturaCompraArchivo(
  fuf: string,
  tipo: "pdf" | "xml",
): Promise<void> {
  const res = await fetchAuth(
    `/api/etrm/facturas-compra/${encodeURIComponent(fuf)}/archivo?tipo=${tipo}`,
    { credentials: "include" },
  );
  if (!res.ok) {
    let detail = `${res.status} ${res.statusText}`;
    try {
      const j = await res.json();
      if (j?.detail) detail = j.detail;
    } catch {
      /* ignore */
    }
    throw new Error(detail);
  }
  const blob = await res.blob();
  const objectUrl = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = objectUrl;
  const disp = res.headers.get("Content-Disposition") ?? "";
  const m = /filename="([^"]+)"/.exec(disp);
  a.download = m ? m[1] : `${fuf}.${tipo}`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(objectUrl);
}

/** Descarga un ZIP con los PDF y XML de las facturas de compra seleccionadas. */
export async function descargarFacturasCompraZip(fufs: string[]): Promise<void> {
  const res = await fetchAuth("/api/etrm/facturas-compra/zip", {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ fufs }),
  });
  if (!res.ok) {
    let detail = `${res.status} ${res.statusText}`;
    try {
      const j = await res.json();
      if (j?.detail) detail = j.detail;
    } catch {
      /* ignore */
    }
    throw new Error(detail);
  }
  const blob = await res.blob();
  const objectUrl = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = objectUrl;
  const disp = res.headers.get("Content-Disposition") ?? "";
  const m = /filename="([^"]+)"/.exec(disp);
  a.download = m ? m[1] : "facturas_compra.zip";
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(objectUrl);
}

// ---------- Grupos Back-to-Back (calce compra ↔ venta, etrm.b2b_grupo) --------

/** Membresía de un contrato en un grupo. `producto` = solo esa pata entra al
 * grupo (contratos híbridos como 001MPP/RIC: energía con un back, potencia con
 * otro); null = el contrato completo. */
export type B2bMiembro = {
  id_contrato: number;
  producto: string | null;
  /** cantidad contratada por pata que entra al grupo (p. ej. 5 MW-año) */
  volumen?: { producto: string; cantidad: number; unidad: string; anios: number }[];
};

export type B2bGrupo = {
  id_grupo: number;
  nombre: string;
  notas: string | null;
  creado_por: string;
  creado_en: string;
  actualizado_por: string | null;
  actualizado_en: string | null;
  contratos: number[]; // ids de contrato miembros (compras y ventas)
  miembros: B2bMiembro[]; // igual que `contratos` pero con la pata asignada
  /** posición neta en VOLUMEN por producto (compra − venta; 0 = calzado) */
  posicion?: B2bPosicion[];
};

export type B2bPosicion = {
  producto: string; // Energía | Potencia | CEL | i-REC
  unidad: string; // MWh/h | MW-año | CEL | i-REC
  compra: number;
  venta: number;
  neto: number;
  folios: { compra: string[]; venta: string[] };
  /** solo Energía: volumen × horas de la ventana de pagos */
  compra_mwh?: number;
  venta_mwh?: number;
  neto_mwh?: number;
};

export type B2bGrupoInput = {
  nombre: string;
  notas?: string | null;
  contratos: number[];
  /** pata por contrato ({id: "Energía"|"Potencia"|"CEL"}); ausente = completo */
  productos?: Record<number, string | null>;
};

export function getB2bGrupos(): Promise<{ items: B2bGrupo[] }> {
  return jsonFetch<{ items: B2bGrupo[] }>("/api/etrm/b2b");
}

export type B2bInventario = ReporteInventario & {
  grupo: { id_grupo: number; nombre: string };
  productos_disponibles: string[];
};

/** Balanza del grupo: inventario MENSUAL del producto con solo los contratos del
 *  grupo, acotado a las fechas en que operan. `producto` vacío = el primero. */
export function getB2bInventario(idGrupo: number, producto?: string | null): Promise<B2bInventario> {
  const p = new URLSearchParams();
  if (producto) p.set("producto", producto);
  const qs = p.toString();
  return jsonFetch<B2bInventario>(`/api/etrm/b2b/${idGrupo}/inventario${qs ? `?${qs}` : ""}`);
}

export function crearB2bGrupo(body: B2bGrupoInput): Promise<{ id_grupo: number }> {
  return jsonPost("/api/etrm/b2b", body);
}

export function actualizarB2bGrupo(
  id: number,
  body: B2bGrupoInput,
): Promise<{ id_grupo: number }> {
  return jsonPut(`/api/etrm/b2b/${id}`, body);
}

export function borrarB2bGrupo(id: number): Promise<{ ok: boolean }> {
  return jsonDelete(`/api/etrm/b2b/${id}`);
}

export type B2bUtilidadProducto = {
  producto: string;
  moneda: string;
  ventas: number;
  compras: number;
  ventas_real: number;
  ventas_proy: number;
  compras_real: number;
  compras_proy: number;
  vol_ventas: number;
  vol_compras: number;
  vol_calzado: number;
  mwh_ventas: number;
  mwh_compras: number;
  mwh_calzado: number | null; // solo Energía: MWh liquidados calzados (base del margen)
  no_calculables: number;
  convertidos: number;
  completo: boolean; // false = utilidad PARCIAL (hay contratos sin precio determinista)
  utilidad: number;
  utilidad_real: number;
  utilidad_proy: number;
  margen_ponderado: number | null; // $/MW-año (Potencia), $/CEL, $/MWh liquidado (Energía)
  nota: string | null;
};

export type B2bUtilidadBucket = {
  moneda: string;
  bucket: string; // "2028" o "2028-01"
  ventas: number;
  compras: number;
  ventas_real: number;
  ventas_proy: number;
  compras_real: number;
  compras_proy: number;
  utilidad: number;
  utilidad_real: number;
  utilidad_proy: number;
  proyectado: boolean; // true si el bucket no tiene nada en firme
} & Record<string, number | string | boolean>; // claves s{i}_real / s{i}_proy por contraparte

export type B2bUtilidadSerie = {
  key: string; // "s0", "s1", … (en el bucket: `${key}_real` / `${key}_proy`)
  moneda: string;
  contraparte: string;
  lado: "ventas" | "compras";
  producto: string; // permite filtrar la gráfica por producto sin refetch
};

export type B2bUtilidadContrato = {
  id_contrato: number;
  folio: string;
  contraparte: string;
  rol_mpp: string;
  grupo: string | null;
  producto: string;
  moneda: string;
  volumen: number | null;
  unidad_vol: string | null;
  total: number | null;
  metodo: string | null;
  calculable: boolean;
  motivo: string | null;
  certeza: boolean; // true = monto en firme; false = proyección o no calculable
  moneda_origen: string;
  convertido: boolean; // true = el monto se convirtió al FIX (viene de otra moneda)
  lado_flujo: "ventas" | "compras"; // flujo real: en energía va invertido al rol
  /** monto por periodo (misma agrupación y moneda que la gráfica): {"2026-06": 1234} */
  serie: Record<string, number>;
  /** solo en filas "(proyección cargada)": plan COMPLETO del periodo — la serie
   *  muestra el residual (plan − en firme) y este es el tooltip. */
  serie_plan?: Record<string, number> | null;
  /** 'cenace' = liquida en el estado de cuenta CENACE (pata MEM / TBFin
   *  automática); 'contraparte' = movimiento fuera del edocta (strike,
   *  potencia, CEL, proyecciones). */
  fuente_mov?: "cenace" | "contraparte";
  /** el monto sale de las proyecciones cargadas en /proyecciones (icono P) */
  de_proyecciones?: boolean;
};

export type B2bUtilidad = {
  agrupacion: "anual" | "mensual";
  moneda: string; // moneda de la vista: TODOS los montos vienen en ésta
  fx: { fecha: string | null; valor: number }; // TC de conversión (fecha null = manual)
  por_producto: B2bUtilidadProducto[];
  buckets: B2bUtilidadBucket[];
  series: B2bUtilidadSerie[];
  contratos: B2bUtilidadContrato[];
};

/** Filtro por etapa del contrato (ventana de pagos vs hoy). Ejes independientes:
 *  futuros (los que aún no pagan) y el corte "Iniciar el corte en" (sustituyó
 *  al checkbox "Incluir contratos terminados", 2026-09-03). */
export type B2bEtapas = {
  incluirFuturos: boolean; // false = solo vigentes (descarta los que no han iniciado)
  corteDesde?: string; // YYYY-MM-DD (día 1): solo movimientos DESDE ese mes; vacío = todo
};

/** "Mostrar transacciones con" (filtro de TODO el reporte /b2b/utilidad, se
 *  aplica en el backend): 'cenace' = solo lo que liquida en el estado de cuenta
 *  CENACE (pata MEM / TBFin automática); 'contraparte' = solo movimientos fuera
 *  del edocta (strike, potencia, CEL, proyecciones); 'ambas' = todo. */
export type B2bFuenteMov = "ambas" | "cenace" | "contraparte";

function etapasQS(etapas?: B2bEtapas): string {
  if (!etapas) return "";
  return (
    `&incluir_futuros=${etapas.incluirFuturos}` +
    (etapas.corteDesde ? `&corte_desde=${etapas.corteDesde}` : "")
  );
}

/** Pagos/cobros vía TBF en el edocta que no son liquidaciones diarias de
 *  energía (p. ej. Blue pagando sus facturas de potencia). Informativo. */
export type TbfCenaceItem = {
  folio: string;
  cuenta: string;
  contraparte: string;
  direccion: "ingreso" | "egreso";
  fecha_oper: string;
  fecha_pago: string | null;
  monto_mxn: number;
  mwh: number;
};

export function getTbfCenace(): Promise<{ items: TbfCenaceItem[] }> {
  return jsonFetch<{ items: TbfCenaceItem[] }>("/api/etrm/b2b/tbf-cenace");
}

/** Meses con TBFin liquidada sin documento a strike, por dónde se captura:
 *  ventas → /facturacion, compras → /sombra (bolitas rojas del menú). */
export type B2bPendientes = {
  // meses = mes de PAGO (dashboard); meses_captura = mes de OPERACIÓN a
  // capturar en /facturacion o /sombra (difieren con strike_mes_pago_offset)
  facturacion: { folio: string; meses: string[]; meses_captura: string[] }[];
  sombra: { folio: string; meses: string[]; meses_captura: string[] }[];
};

export function getB2bPendientes(): Promise<B2bPendientes> {
  return jsonFetch<B2bPendientes>("/api/etrm/b2b/pendientes");
}

/** Detalle diario de la pata MEM (abono/cargo) de un contrato de energía. */
export type MemDetalleItem = {
  fecha_operacion: string;
  mwh: number;
  monto_mxn: number;
  fecha_pago: string | null;
  pago_estimado?: boolean; // día aún sin edocta: fecha por calendario CENACE (miércoles de la 3ª semana)
  mes: string; // columna del dashboard donde cae el día
  fuente: string;
};

export type MemDetalle = {
  id_contrato: number;
  folio: string;
  direccion: string;
  moneda: string;
  items: MemDetalleItem[];
  total: number;
};

export function getMemDetalle(contrato: number | string): Promise<MemDetalle> {
  return jsonFetch<MemDetalle>(
    `/api/etrm/b2b/utilidad/mem-detalle?contrato=${encodeURIComponent(String(contrato))}`,
  );
}

export function getB2bUtilidad(
  agrupacion: "anual" | "mensual",
  moneda: "USD" | "MXN",
  respaldoTbfin: boolean,
  grupo?: number | null, // id_grupo B2B: limita el libro a ese back-to-back
  etapas?: B2bEtapas,
  incluirProyecciones = true, // proyecciones cargadas en /proyecciones
  proyeccionesHasta?: string, // tope YYYY-MM-DD: solo proyecciones hasta esa fecha
  tc?: number, // TC manual para la conversión (default UI 17.5000); sin él, FIX
  fuente: B2bFuenteMov = "ambas", // "Mostrar transacciones con": CENACE / contraparte
): Promise<B2bUtilidad> {
  return jsonFetch<B2bUtilidad>(
    `/api/etrm/b2b/utilidad?agrupacion=${agrupacion}&moneda=${moneda}&respaldo_tbfin=${respaldoTbfin}` +
      (grupo != null ? `&grupo=${grupo}` : "") +
      etapasQS(etapas) +
      `&incluir_proyecciones=${incluirProyecciones}` +
      (proyeccionesHasta ? `&proyecciones_hasta=${proyeccionesHasta}` : "") +
      (tc != null ? `&tc=${tc}` : "") +
      (fuente !== "ambas" ? `&fuente=${fuente}` : ""),
  );
}

/** Descarga el Detalle por contrato como .xlsx (mismos parámetros del dashboard). */
export async function descargarB2bUtilidadXlsx(
  agrupacion: "anual" | "mensual",
  moneda: "USD" | "MXN",
  respaldoTbfin: boolean,
  grupo?: number | null,
  etapas?: B2bEtapas,
  incluirProyecciones = true,
  proyeccionesHasta?: string,
  tc?: number,
  fuente: B2bFuenteMov = "ambas",
): Promise<void> {
  const url =
    `/api/etrm/b2b/utilidad/xlsx?agrupacion=${agrupacion}&moneda=${moneda}` +
    `&respaldo_tbfin=${respaldoTbfin}` +
    (grupo != null ? `&grupo=${grupo}` : "") +
    etapasQS(etapas) +
    `&incluir_proyecciones=${incluirProyecciones}` +
    (proyeccionesHasta ? `&proyecciones_hasta=${proyeccionesHasta}` : "") +
    (tc != null ? `&tc=${tc}` : "") +
    (fuente !== "ambas" ? `&fuente=${fuente}` : "");
  const res = await fetchAuth(url, { credentials: "include" });
  if (!res.ok) {
    let detail = `${res.status} ${res.statusText}`;
    try {
      const j = await res.json();
      if (j?.detail) detail = j.detail;
    } catch {
      /* ignore */
    }
    throw new Error(detail);
  }
  const blob = await res.blob();
  const objectUrl = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = objectUrl;
  const disp = res.headers.get("Content-Disposition") ?? "";
  const m = /filename="([^"]+)"/.exec(disp);
  a.download = m ? m[1] : `utilidad_b2b_detalle_${agrupacion}_${moneda}.xlsx`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(objectUrl);
}

// ---------- Facturas Recibidas (carga manual de CFDI, etrm.factura_recibida) --

export type FacturaRecibidaItem = {
  id: number;
  origen: "cfdi" | "manual" | "extranjera";
  uuid: string | null;
  emisor_pais: string | null;   // solo extranjeras (mig 092)
  emisor_tax_id: string | null; // Tax ID / VAT del emisor extranjero
  version_cfdi: string | null;
  serie: string | null;
  folio: string | null;
  fecha: string | null;
  tipo_comprobante: string | null;
  metodo_pago: string | null;
  forma_pago: string | null;
  moneda: string | null;
  tipo_cambio: number | null;
  subtotal: number | null;
  total_impuestos: number | null;
  total: number | null;
  emisor_rfc: string | null;
  emisor_nombre: string | null;
  receptor_rfc: string | null;
  receptor_nombre: string | null;
  receptor_uso_cfdi: string | null;
  fecha_timbrado: string | null;
  descripcion: string | null;
  nombre_archivo_xml: string | null;
  nombre_archivo_pdf: string | null;
  tiene_xml: boolean;
  tiene_pdf: boolean;
  subido_por: string | null;
  subido_en: string;
  fecha_pago_programada: string | null; // cuándo se DEBE pagar (captura manual)
  pagada_en: string | null;             // fecha real de pago (null = pendiente)
  pagada_por: string | null;
};

export type FacturaRecibidaConcepto = {
  num_lin: number;
  clave_prod_serv: string | null;
  no_identificacion: string | null;
  descripcion: string | null;
  cantidad: number | null;
  unidad: string | null;
  valor_unitario: number | null;
  importe: number | null;
};

export function getFacturasRecibidas(): Promise<{ items: FacturaRecibidaItem[] }> {
  return jsonFetch<{ items: FacturaRecibidaItem[] }>("/api/etrm/facturas-recibidas");
}

export function getFacturaRecibidaConceptos(id: number): Promise<FacturaRecibidaConcepto[]> {
  return jsonFetch<FacturaRecibidaConcepto[]>(`/api/etrm/facturas-recibidas/${id}/conceptos`);
}

/** Sube una factura (XML CFDI y/o PDF; sin XML van los campos manuales en el FormData). */
export async function subirFacturaRecibida(fd: FormData): Promise<FacturaRecibidaItem> {
  const res = await fetchAuth("/api/etrm/facturas-recibidas", {
    method: "POST",
    credentials: "include",
    body: fd,
  });
  if (!res.ok) {
    let detail = `${res.status} ${res.statusText}`;
    try {
      const j = await res.json();
      if (j?.detail) detail = typeof j.detail === "string" ? j.detail : JSON.stringify(j.detail);
    } catch {
      /* ignore */
    }
    throw new Error(detail);
  }
  return res.json();
}

/** Captura manual de una factura EXTRANJERA (sin CFDI): campos + PDF en el FormData. */
export async function subirFacturaExtranjera(fd: FormData): Promise<FacturaRecibidaItem> {
  const res = await fetchAuth("/api/etrm/facturas-recibidas/extranjera", {
    method: "POST",
    credentials: "include",
    body: fd,
  });
  if (!res.ok) throw new Error(await detalleDe(res));
  return res.json();
}

/** FIX Banxico USD/MXN vigente en la fecha (para prellenar el tipo de cambio). */
export function getFixFecha(fecha: string): Promise<{ fecha: string | null; valor: number | null }> {
  return jsonFetch(`/api/etrm/facturas-recibidas/fix?fecha=${fecha}`);
}

export function borrarFacturaRecibida(id: number): Promise<{ id: number; eliminada: boolean }> {
  return jsonDelete(`/api/etrm/facturas-recibidas/${id}`);
}

export function programarPagoFacturaRecibida(
  id: number,
  fecha: string | null,
): Promise<{ id: number; fecha_pago_programada: string | null }> {
  return jsonPut(`/api/etrm/facturas-recibidas/${id}/pago-programado`, { fecha });
}

export function marcarPagadaFacturaRecibida(
  id: number,
  pagada: boolean,
): Promise<{ id: number; pagada: boolean }> {
  return jsonPut(`/api/etrm/facturas-recibidas/${id}/pagada`, { pagada });
}

/** Descarga el formato FSP (Solicitud de Pago, PDF) llenado con la factura. */
export async function descargarFacturaRecibidaFsp(id: number): Promise<void> {
  const res = await fetchAuth(`/api/etrm/facturas-recibidas/${id}/fsp`, {
    credentials: "include",
  });
  if (!res.ok) {
    let detail = `${res.status} ${res.statusText}`;
    try {
      const j = await res.json();
      if (j?.detail) detail = j.detail;
    } catch {
      /* ignore */
    }
    throw new Error(detail);
  }
  const blob = await res.blob();
  const objectUrl = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = objectUrl;
  const disp = res.headers.get("Content-Disposition") ?? "";
  const m = /filename="([^"]+)"/.exec(disp);
  a.download = m ? m[1] : `FSP_${id}.pdf`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(objectUrl);
}

export async function descargarFacturaRecibidaArchivo(
  id: number,
  tipo: "pdf" | "xml",
): Promise<void> {
  const res = await fetchAuth(`/api/etrm/facturas-recibidas/${id}/archivo?tipo=${tipo}`, {
    credentials: "include",
  });
  if (!res.ok) {
    let detail = `${res.status} ${res.statusText}`;
    try {
      const j = await res.json();
      if (j?.detail) detail = j.detail;
    } catch {
      /* ignore */
    }
    throw new Error(detail);
  }
  const blob = await res.blob();
  const objectUrl = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = objectUrl;
  const disp = res.headers.get("Content-Disposition") ?? "";
  const m = /filename="([^"]+)"/.exec(disp);
  a.download = m ? m[1] : `factura_${id}.${tipo}`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(objectUrl);
}

export function getHistoricoConsumo(contrato: string): Promise<HistoricoMes[]> {
  return jsonFetch<HistoricoMes[]>(
    `/api/etrm/facturacion/historico?contrato=${encodeURIComponent(contrato)}`,
  );
}

/** Descarga el PDF de la factura del periodo (con auth). */
export async function descargarFacturaPdf(
  contrato: string,
  desde: string,
  hasta: string,
): Promise<void> {
  const p = new URLSearchParams({ contrato, desde, hasta });
  const res = await fetchAuth(`/api/etrm/facturacion/pdf?${p.toString()}`, {
    credentials: "include",
  });
  if (!res.ok) {
    let detail = `${res.status} ${res.statusText}`;
    try {
      const j = await res.json();
      if (j?.detail) detail = j.detail;
    } catch {
      /* ignore */
    }
    throw new Error(detail);
  }
  const blob = await res.blob();
  const objectUrl = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = objectUrl;
  const disp = res.headers.get("Content-Disposition") ?? "";
  const m = /filename="([^"]+)"/.exec(disp);
  a.download = m ? m[1] : `factura_${desde}_${hasta}.pdf`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(objectUrl);
}

// ---------- Sombra CENACE (TBFin vs Estado de Cuenta) ------------------------

export type SombraCenaceItem = {
  id_contrato: number;
  contrato: string;
  fecha: string;
  folio_cenace: string;
  estatus_cenace: string | null;
  // TBFin automática (p. ej. Atria): sin folio propio — se compara el MODELO
  // del motor vs el edocta (el folio se descubre en el estado de cuenta).
  automatica?: boolean;
  nota?: string | null;
  rol_mpp: string; // 'vendedor' = TBFin EMITIDA por MPP; 'comprador' = RECIBIDA
  nodo_contrato: string;
  monto_enviado: number;
  mwh_etrm: number;
  monto_mda_bd: number;      // MWh TBFin × PML MDA (public.pml_mda) del nodo del contrato
  pml_incompleto: boolean;
  encontrado: boolean;
  nodo_edocta: string | null;
  mwh_edocta: number | null;
  monto_edocta: number | null;
  nodo_ok: boolean | null;
  mwh_ok: boolean | null;
  monto_ok: boolean | null;
  estado: "ok" | "difiere" | "sin_edocta";
};

export type SombraCenaceResumen = {
  anio: number;
  mes: number;
  edocta_hasta: string | null;
  totales: { tbfins: number; ok: number; difieren: number; sin_edocta: number };
  items: SombraCenaceItem[];
};

export type SombraCenaceHora = {
  hora: number;
  mwh_etrm: number;
  pml_bd: number | null;
  monto_esperado: number | null;
  nodo_edocta: string | null;
  mwh_edocta: number | null;
  precio_edocta: number | null;
  monto_edocta: number | null;
  ok: boolean | null;
};

export type SombraCenaceDetalle = {
  contrato: string;
  fecha: string;
  folio_cenace: string;
  estatus_cenace: string | null;
  nodo_contrato: string;
  horas: SombraCenaceHora[];
};

export function getSombraCenace(anio: number, mes: number): Promise<SombraCenaceResumen> {
  return jsonFetch<SombraCenaceResumen>(`/api/etrm/sombra-cenace?anio=${anio}&mes=${mes}`);
}

export function getSombraCenaceDetalle(
  idContrato: number,
  fecha: string,
): Promise<SombraCenaceDetalle> {
  return jsonFetch<SombraCenaceDetalle>(
    `/api/etrm/sombra-cenace/detalle?contrato=${idContrato}&fecha=${fecha}`,
  );
}

// ---------- Facturas de SERVICIOS de consultoría (mig 086) -------------------

/** Alta del registro de proveedores del Portal (datos fiscales del receptor). */
export type ProveedorAlta = {
  id: number;
  razon_social: string | null;
  nombre_comercial: string | null;
  rfc: string | null;
  regimen_fiscal: string | null;
  cp: string | null;
  contacto_correo: string | null;
  enviado_en: string | null;
};

export type ConceptoServicio = {
  descripcion: string;
  cantidad: number;
  unidad?: string;
  clave_prod_serv?: string;
  precio_unitario: number;
  importe?: number;
};

export type FacturaServicioItem = {
  id: number;
  receptor_rfc: string;
  receptor_nombre: string;
  receptor_regimen: string | null;
  receptor_cp: string | null;
  receptor_correo: string | null;
  id_proveedor_alta: number | null;
  fecha_emision: string;
  fecha_limite_pago: string | null;
  moneda: string;
  iva_pct: number;
  subtotal: number;
  iva: number;
  total: number;
  conceptos: ConceptoServicio[];
  notas: string | null;
  folio_cfdi: string | null;   // lo pone Flamma al timbrar
  uuid_cfdi: string | null;
  cobrada_en: string | null;   // null = pendiente de cobro
  cobrada_por: string | null;
  creado_por: string | null;
  creado_en: string;
};

export type FacturaServicioInput = {
  receptor_rfc: string;
  receptor_nombre: string;
  receptor_regimen?: string | null;
  receptor_cp?: string | null;
  receptor_correo?: string | null;
  id_proveedor_alta?: number | null;
  fecha_emision: string;
  fecha_limite_pago?: string | null;
  moneda: string;
  iva_pct: number;
  conceptos: ConceptoServicio[];
  notas?: string | null;
};

export function getProveedoresAlta(q?: string): Promise<ProveedorAlta[]> {
  return jsonFetch<ProveedorAlta[]>(
    `/api/etrm/facturas-servicios/proveedores${q ? `?q=${encodeURIComponent(q)}` : ""}`,
  );
}

export function getFacturasServicios(): Promise<FacturaServicioItem[]> {
  return jsonFetch<FacturaServicioItem[]>("/api/etrm/facturas-servicios/lista");
}

export function crearFacturaServicio(
  payload: FacturaServicioInput,
): Promise<FacturaServicioItem> {
  return jsonPost<FacturaServicioItem>("/api/etrm/facturas-servicios", payload);
}

/** Completa lo que llega después de emitir (folio/UUID de Flamma) o corrige fecha/notas. */
export function editarFacturaServicio(
  id: number,
  campos: { folio_cfdi?: string; uuid_cfdi?: string; fecha_limite_pago?: string; notas?: string },
): Promise<FacturaServicioItem> {
  const p = new URLSearchParams();
  for (const [k, v] of Object.entries(campos)) if (v) p.set(k, v);
  return jsonFetch<FacturaServicioItem>(
    `/api/etrm/facturas-servicios/${id}?${p.toString()}`,
    { method: "PATCH" },
  );
}

/** Marca la factura como cobrada (fecha) o la regresa a pendiente (null). */
export function cobrarFacturaServicio(
  id: number,
  cobradaEn: string | null,
): Promise<FacturaServicioItem> {
  return jsonFetch<FacturaServicioItem>(`/api/etrm/facturas-servicios/${id}/cobro`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ cobrada_en: cobradaEn }),
  });
}

export function borrarFacturaServicio(id: number): Promise<{ id: number; eliminada: boolean }> {
  return jsonDelete(`/api/etrm/facturas-servicios/${id}`);
}

/** Descarga el PDF de soporte de la factura de honorarios (con auth). */
export async function descargarFacturaServicioPdf(id: number): Promise<void> {
  const res = await fetchAuth(`/api/etrm/facturas-servicios/${id}/pdf`, {
    credentials: "include",
  });
  if (!res.ok) {
    let detail = `${res.status} ${res.statusText}`;
    try {
      const j = await res.json();
      if (j?.detail) detail = j.detail;
    } catch {
      /* respuesta sin JSON */
    }
    throw new Error(detail);
  }
  const blob = await res.blob();
  const objectUrl = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = objectUrl;
  const disp = res.headers.get("Content-Disposition") ?? "";
  const m = /filename="([^"]+)"/.exec(disp);
  a.download = m ? m[1] : `honorarios_${id}.pdf`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(objectUrl);
}

// ---------- Oficios: generador de folio -------------------------------------

export type OficioArea = { clave: string; nombre: string; activo: boolean };

export type OficioDestinatario = {
  id: number;
  clave: string;
  nombre: string;
  activo: boolean;
};

export type Oficio = {
  id: number;
  folio: string;
  fecha: string;
  area_clave: string;
  area_nombre: string | null;
  consecutivo: number;
  destinatario_id: number;
  destinatario_clave: string | null;
  destinatario_nombre: string | null;
  solicitante: string;
  solicitante_email: string;
  titulo: string;
  motivo: string | null;
  tags: string[];
  creado_en: string;
};

export type OficioInput = {
  fecha: string;
  area_clave: string;
  destinatario_id: number;
  titulo: string;
  motivo?: string | null;
  tags: string[];
};

export type OficioFiltros = {
  q?: string;
  tags?: string[];
  area?: string;
  desde?: string;
  hasta?: string;
};

export function getOficioAreas(soloActivas = false): Promise<OficioArea[]> {
  return jsonFetch<OficioArea[]>(
    `/api/etrm/oficios/areas${soloActivas ? "?solo_activas=true" : ""}`,
  );
}

export function guardarOficioArea(payload: {
  clave: string;
  nombre: string;
  activo: boolean;
}): Promise<OficioArea> {
  return jsonPost<OficioArea>("/api/etrm/oficios/areas", payload);
}

export function getOficioDestinatarios(soloActivos = false): Promise<OficioDestinatario[]> {
  return jsonFetch<OficioDestinatario[]>(
    `/api/etrm/oficios/destinatarios${soloActivos ? "?solo_activos=true" : ""}`,
  );
}

export function crearOficioDestinatario(payload: {
  clave: string;
  nombre: string;
  activo: boolean;
}): Promise<OficioDestinatario> {
  return jsonPost<OficioDestinatario>("/api/etrm/oficios/destinatarios", payload);
}

export function actualizarOficioDestinatario(
  id: number,
  payload: { clave: string; nombre: string; activo: boolean },
): Promise<OficioDestinatario> {
  return jsonPut<OficioDestinatario>(`/api/etrm/oficios/destinatarios/${id}`, payload);
}

/** Etiquetas ya usadas, de la más frecuente a la menos. */
export function getOficioTags(): Promise<string[]> {
  return jsonFetch<string[]>("/api/etrm/oficios/tags");
}

export function getOficios(f: OficioFiltros = {}): Promise<Oficio[]> {
  const p = new URLSearchParams();
  if (f.q) p.set("q", f.q);
  for (const t of f.tags ?? []) p.append("tags", t);
  if (f.area) p.set("area", f.area);
  if (f.desde) p.set("desde", f.desde);
  if (f.hasta) p.set("hasta", f.hasta);
  const qs = p.toString();
  return jsonFetch<Oficio[]>(`/api/etrm/oficios/lista${qs ? `?${qs}` : ""}`);
}

/** Vista previa del folio (el definitivo se asigna al guardar). */
export function getProximoFolio(area: string, fecha: string): Promise<string> {
  return jsonFetch<string>(
    `/api/etrm/oficios/proximo-folio?area=${encodeURIComponent(area)}&fecha=${fecha}`,
  );
}

export function crearOficio(payload: OficioInput): Promise<Oficio> {
  return jsonPost<Oficio>("/api/etrm/oficios", payload);
}

export function actualizarOficio(id: number, payload: OficioInput): Promise<Oficio> {
  return jsonPut<Oficio>(`/api/etrm/oficios/${id}`, payload);
}

export function borrarOficio(id: number): Promise<{ ok: boolean }> {
  return jsonDelete(`/api/etrm/oficios/${id}`);
}

// ---------- Pronósticos Luxem (carga del Excel de demanda horaria) ---------
/** Resumen de una carga dentro de una pestaña de la previa. */
export type PronLuxCargaResumen = {
  carga: number;
  horas: number;
  suma: number;
  minimo: number;
  maximo: number;
  promedio: number;
};

/** Una pestaña del Excel tal como la entendió el backend. */
export type PronLuxHoja = {
  hoja: string;
  ignorada: boolean;
  motivo: string | null;
  fechas: string[];
  horas: number;
  filas: number;
  cargas: PronLuxCargaResumen[];
  detalle: { fecha: string; hora: number; carga: number; pronostico: number }[];
};

export type PronLuxPrevia = {
  archivo: string;
  hojas: PronLuxHoja[];
  fechas: string[];
  cargas: number[];
  total_filas: number;
  dias_existentes: number; // días del archivo que YA están en la BD (se pisarán)
};

export type PronLuxDia = {
  fecha: string;
  filas: number;
  horas: number;
  cargas: number[];
  suma: number;
  archivo: string | null;
  cargado_por: string | null;
  cargado_en: string;
};

/** Sube el .xlsx solo para ver qué trae; NO escribe en la BD. */
export async function previaPronosticosLuxem(fd: FormData): Promise<PronLuxPrevia> {
  const res = await fetchAuth("/api/etrm/pronosticos-luxem/previa", { method: "POST", body: fd });
  if (!res.ok) throw new Error(await detalleDe(res));
  return res.json();
}

/** Guarda el .xlsx (upsert por fecha+hora+carga). */
export async function cargarPronosticosLuxem(fd: FormData): Promise<{
  guardadas: number;
  dias: string[];
  cargas: number[];
  hojas_leidas: string[];
  hojas_ignoradas: string[];
}> {
  const res = await fetchAuth("/api/etrm/pronosticos-luxem/cargar", { method: "POST", body: fd });
  if (!res.ok) throw new Error(await detalleDe(res));
  return res.json();
}

export function getPronosticosLuxemResumen(): Promise<{ items: PronLuxDia[] }> {
  return jsonFetch<{ items: PronLuxDia[] }>("/api/etrm/pronosticos-luxem/resumen");
}

/** Los pronósticos guardados de un día (para el detalle de la tabla). */
export function getPronosticosLuxemDia(
  fecha: string,
): Promise<{ items: { fecha: string; hora: number; carga: number; pronostico: number }[] }> {
  return jsonFetch(`/api/etrm/pronosticos-luxem?desde=${fecha}&hasta=${fecha}`);
}

export function borrarDiaPronosticoLuxem(
  fecha: string,
): Promise<{ fecha: string; eliminadas: number }> {
  return jsonDelete(`/api/etrm/pronosticos-luxem/dia/${fecha}`);
}

// ---------- Arbitraje (demanda fija diaria, plantilla CENACE OCEvr1) --------
export type ArbSistema = "SIN" | "BCA" | "BCS";

export type ArbContraparte = { id_contraparte: number; nombre: string; alias: string | null };

export type ArbHora = { hora: number; demanda_mw: number };

export type ArbPrevia = {
  archivo: string;
  id_contraparte: number;
  contraparte: string;
  fecha: string;
  centro_carga: string;
  sistema_archivo: ArbSistema | null; // el botón marcado en el Excel (la plantilla trae BCA)
  horas: ArbHora[];
  resumen: { mwh: number; maximo: number; hora_pico: number; minimo: number; promedio: number };
  avisos: string[];
  ya_cargado: boolean; // ese día+CC ya existe para la contraparte (se reemplaza)
};

export type ArbDia = {
  id_contraparte: number;
  contraparte: string;
  fecha: string;
  centro_carga: string;
  sistema: ArbSistema;
  horas: number;
  mwh: number;
  maximo: number;
  archivo: string | null;
  cargado_por: string | null;
  cargado_en: string;
  var_usd: number | null;        // del VaR guardado, si existe
  cvar_usd: number | null;
  var_confianza: number | null;
};

/** VaR DART de un día de arbitraje: racionales + resultados (etrm.arbitraje_var, mig 110). */
export type ArbVar = {
  id_contraparte: number;
  fecha: string;                 // fecha de operación = la del arbitraje
  centro_carga: string;
  punto_tipo: TipoPuntoCauchy;
  punto_sistema: string;
  punto_clave: string;
  punto_etiqueta: string | null;
  fecha_calculo: string;
  ventana_dias: number;
  location: number;
  scale: number;
  lh_min: number;
  lh_max: number;
  tc: number;
  confianza: number;
  n_sim: number;
  semilla: number;
  trading_mwh: number[];         // con signo (−demanda)
  volumen_mwh: number;
  var_usd: number | null;
  cvar_usd: number | null;
  var_mwh_usd: number | null;
  cvar_mwh_usd: number | null;
  pct_sup_usd: number | null;
  prom_sup_usd: number | null;
  util_esp_usd: number | null;
  perd_esp_usd: number | null;
  util_neta_usd: number | null;
  media_usd: number | null;
  p50_usd: number | null;
  dart_medio: number[] | null;
  calculado_por: string | null;
  calculado_en: string;
};

export function getArbitrajeContrapartes(): Promise<ArbContraparte[]> {
  return jsonFetch<ArbContraparte[]>("/api/etrm/arbitraje/contrapartes");
}

/** Sube el .xlsx solo para ver qué trae; NO escribe en la BD. */
export async function previaArbitraje(fd: FormData): Promise<ArbPrevia> {
  const res = await fetchAuth("/api/etrm/arbitraje/previa", { method: "POST", body: fd });
  if (!res.ok) throw new Error(await detalleDe(res));
  return res.json();
}

/** Guarda el día (reemplaza lo que hubiera de esa contraparte+fecha+CC). */
export async function cargarArbitraje(fd: FormData): Promise<{
  fecha: string;
  centro_carga: string;
  sistema: ArbSistema;
  guardadas: number;
  reemplazadas: number;
  mwh: number;
  var: ArbVar | null;          // VaR DART corrido al guardar
  var_error: string | null;    // por qué no se calculó (el día sí se guardó)
}> {
  const res = await fetchAuth("/api/etrm/arbitraje/cargar", { method: "POST", body: fd });
  if (!res.ok) throw new Error(await detalleDe(res));
  return res.json();
}

export function getArbitrajeResumen(): Promise<{ items: ArbDia[] }> {
  return jsonFetch<{ items: ArbDia[] }>("/api/etrm/arbitraje/resumen");
}

const arbClave = (d: { id_contraparte: number; fecha: string; centro_carga: string }) =>
  new URLSearchParams({
    id_contraparte: String(d.id_contraparte), fecha: d.fecha, centro_carga: d.centro_carga,
  }).toString();

export function getArbitrajeDetalle(
  d: { id_contraparte: number; fecha: string; centro_carga: string },
): Promise<{ horas: ArbHora[] }> {
  return jsonFetch<{ horas: ArbHora[] }>(`/api/etrm/arbitraje/detalle?${arbClave(d)}`);
}

export function borrarArbitraje(
  d: { id_contraparte: number; fecha: string; centro_carga: string },
): Promise<{ eliminadas: number }> {
  return jsonDelete(`/api/etrm/arbitraje?${arbClave(d)}`);
}

export function getArbitrajeVar(
  d: { id_contraparte: number; fecha: string; centro_carga: string },
): Promise<{ var: ArbVar | null }> {
  return jsonFetch<{ var: ArbVar | null }>(`/api/etrm/arbitraje/var?${arbClave(d)}`);
}

/** Vuelve a correr el VaR del día con el nodo y parámetros vigentes. */
export function recalcularArbitrajeVar(
  d: { id_contraparte: number; fecha: string; centro_carga: string },
): Promise<{ var: ArbVar }> {
  return jsonPost<{ var: ArbVar }>(`/api/etrm/arbitraje/var/recalcular?${arbClave(d)}`, {});
}

// Centros de carga del arbitraje → nodo (PML) o zona de carga (PND), mig 109.
export type ArbCentroCarga = {
  id_contraparte: number;
  contraparte: string;
  alias: string | null;
  centro_carga: string;
  sistema: ArbSistema | null;     // el de la última carga del CC
  dias: number;                   // días cargados (0 = ya no tiene, solo la asignación)
  desde: string | null;
  hasta: string | null;
  mwh: number;
  ultima_carga: string | null;
  tipo: TipoPuntoCauchy | null;   // null = sin nodo asignado
  nodo_sistema: ArbSistema | null;
  clave: string | null;
  nodo_nombre: string | null;     // de public.nodos_p (solo nodos)
  nodo_zona: string | null;
  actualizado_por: string | null;
  actualizado_en: string | null;
};

const ARB_CC = "/api/etrm/arbitraje/centros-carga";

export function getArbitrajeCentrosCarga(): Promise<{ items: ArbCentroCarga[] }> {
  return jsonFetch(ARB_CC);
}

export function getOpcionesNodoArbitraje(
  tipo: TipoPuntoCauchy, sistema: string, q: string,
): Promise<OpcionPuntoCauchy[]> {
  return jsonFetch(`${ARB_CC}/opciones?${new URLSearchParams({ tipo, sistema, q })}`);
}

export function asignarNodoArbitraje(payload: {
  id_contraparte: number; centro_carga: string;
  tipo: TipoPuntoCauchy; sistema: string; clave: string;
}): Promise<{ clave: string; var_calculados: number; var_error: string | null }> {
  return jsonPut(ARB_CC, payload);
}

export function quitarNodoArbitraje(
  d: { id_contraparte: number; centro_carga: string },
): Promise<{ eliminado: boolean }> {
  const qs = new URLSearchParams({ id_contraparte: String(d.id_contraparte), centro_carga: d.centro_carga });
  return jsonDelete(`${ARB_CC}?${qs}`);
}

// ---------- Posiciones de mercado (análisis de mercado, mig 112) ------------

export type LadoPosicion = "demanda" | "oferta";
export type ProductoPosicion = "energia" | "potencia" | "cel" | "irec";

export type ParticipanteMem = {
  clave: string;               // clave CENACE: C006, G009, U003…
  razon_social: string;
  origen: "sim" | "mda" | "contraparte" | "manual";
  alias: string | null;        // alias de la contraparte del ETRM con ese código SIM
  posiciones: number;
};

export type PosicionMercado = {
  id: number;
  participante: string;
  razon_social: string;
  lado: LadoPosicion;
  producto: ProductoPosicion;
  anio: number;
  cantidad: number;            // siempre positiva; el signo lo da el lado
  notas: string | null;
  actualizado_por: string | null;
  actualizado_en: string | null;
};

export type CapturaPosicion = {
  participante: string;
  lado: LadoPosicion;
  producto: ProductoPosicion;
  anio_desde: number;
  anio_hasta: number;
  cantidad: number;
  notas: string | null;
};

const POS_MERCADO = "/api/etrm/posiciones-mercado";

export function getParticipantesMem(): Promise<ParticipanteMem[]> {
  return jsonFetch(`${POS_MERCADO}/participantes`);
}

export function altaParticipanteMem(
  payload: { clave: string; razon_social: string },
): Promise<ParticipanteMem> {
  return jsonPost(`${POS_MERCADO}/participantes`, payload);
}

export function getPosicionesMercado(): Promise<{ items: PosicionMercado[] }> {
  return jsonFetch(POS_MERCADO);
}

export function capturarPosicionMercado(
  payload: CapturaPosicion,
): Promise<{ guardados: number; nuevos: number; reemplazados: number }> {
  return jsonPut(POS_MERCADO, payload);
}

export function borrarPosicionMercado(id: number): Promise<{ eliminado: boolean }> {
  return jsonDelete(`${POS_MERCADO}/${id}`);
}

// ---------- Nominación a contraparte (pronóstico Luxem + arbitraje, mig 116) ---

export type NomDia = {
  fecha: string;
  carga: number;                    // Carga N de Luxem
  id_contraparte: number | null;    // null = la carga no tiene centro de carga asignado
  contraparte: string | null;
  alias: string | null;
  centro_carga: string | null;
  sistema: ArbSistema;
  horas: number;
  pronostico_mwh: number;
  arbitraje_mwh: number;
  nominacion_mwh: number;
  pct_arbitraje: number | null;     // % (34.95 = 34.95 %)
  sin_arbitraje: boolean;           // no hay arbitraje cargado para ese día y CC
  guardada: boolean;
  guardado_por: string | null;
  guardado_en: string | null;
  pronostico_cambio: boolean;       // el pronóstico se recargó después de guardar
};

export type NomHora = {
  hora: number;
  pronostico_mw: number;
  arbitraje_mw: number;
  pct_arbitraje: number | null;
  nominacion_mw: number;
};

export type NomDetalle = {
  fecha: string;
  carga: number;
  id_contraparte: number;
  contraparte: string;
  centro_carga: string;
  sistema: ArbSistema;
  propuesta: NomHora[];
  guardada: NomHora[] | null;
  guardado_por: string | null;
  guardado_en: string | null;
};

export type NomPayload = {
  fecha: string;
  carga: number;
  sistema: ArbSistema;
  horas: { hora: number; arbitraje_mw: number }[];
};

export type NomCarga = {
  carga: number;
  id_contraparte: number | null;
  contraparte: string | null;
  centro_carga: string | null;
  actualizado_por: string | null;
  actualizado_en: string | null;
};

const NOM = "/api/etrm/nominacion";
const nomClave = (d: { fecha: string; carga: number }) =>
  new URLSearchParams({ fecha: d.fecha, carga: String(d.carga) }).toString();

export function getNominacionResumen(r: { desde: string; hasta: string }): Promise<{ items: NomDia[] }> {
  const qs = new URLSearchParams();
  if (r.desde) qs.set("desde", r.desde);
  if (r.hasta) qs.set("hasta", r.hasta);
  return jsonFetch(`${NOM}/resumen?${qs}`);
}

export function getNominacionDetalle(d: { fecha: string; carga: number }): Promise<NomDetalle> {
  return jsonFetch(`${NOM}/detalle?${nomClave(d)}`);
}

export function guardarNominacion(p: NomPayload): Promise<{ guardada: boolean; nominacion_mwh: number }> {
  return jsonPut(NOM, p);
}

export function descartarNominacion(d: { fecha: string; carga: number }): Promise<{ eliminado: boolean }> {
  return jsonDelete(`${NOM}?${nomClave(d)}`);
}

/** Solo nominaciones guardadas: el archivo sale de lo guardado en BD. */
export async function descargarNominacion(p: { fecha: string; carga: number }): Promise<void> {
  const res = await fetchAuth(`${NOM}/archivo?${nomClave(p)}`);
  if (!res.ok) throw new Error(await detalleDe(res));
  const blob = await res.blob();
  const objectUrl = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = objectUrl;
  const m = /filename="([^"]+)"/.exec(res.headers.get("Content-Disposition") ?? "");
  a.download = m ? m[1] : `Nominacion_${p.fecha}.xlsx`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(objectUrl);
}

export function getNominacionCargas(): Promise<{
  items: NomCarga[];
  opciones: { id_contraparte: number; contraparte: string; centro_carga: string }[];
}> {
  return jsonFetch(`${NOM}/cargas`);
}

export function asignarNominacionCarga(p: {
  carga: number; id_contraparte: number; centro_carga: string;
}): Promise<NomCarga> {
  return jsonPut(`${NOM}/cargas`, p);
}

// ---- DART realizado del arbitraje nominado --------------------------------
// P&L MXN = arbitraje × (MTR − MDA): arbitraje > 0 compra en MDA, < 0 venta.

export type DartHora = {
  fecha: string;
  hora: number;
  id_contraparte: number;
  contraparte: string;
  alias: string | null;
  centro_carga: string;
  punto: string;
  pronostico_mw: number;
  arbitraje_mw: number;
  mda: number | null;
  mtr: number | null;
  dart: number | null;
  pl_mxn: number | null;
};

export type DartDia = {
  fecha: string;
  id_contraparte: number;
  contraparte: string;
  alias: string | null;
  centro_carga: string;
  punto: string;
  sin_nodo: boolean;
  horas: number;
  horas_con_mtr: number;
  arbitraje_mwh: number;
  pl_mxn: number | null;
  perdidas_mxn: number;
  ganancias_mxn: number;
  horas_ganancia: number;
};

export type DartNodo = {
  punto: string;
  horas_con_mtr: number;
  arbitraje_mwh: number;
  pl_mxn: number | null;
  horas_ganancia: number;
};

export type DartRealizado = {
  desde: string | null;
  hasta: string | null;
  totales: {
    horas: number; horas_con_mtr: number; arbitraje_mwh: number; pl_mxn: number;
    horas_ganancia: number;
  };
  dias: DartDia[];
  nodos: DartNodo[];
  horas: DartHora[];
  mtr_hasta: { punto: string; fecha: string | null }[];
};

export type DartFiltro = { desde: string; hasta: string; id_contraparte: number | "" };

const dartQs = (f: DartFiltro) => {
  const qs = new URLSearchParams();
  if (f.desde) qs.set("desde", f.desde);
  if (f.hasta) qs.set("hasta", f.hasta);
  if (f.id_contraparte !== "") qs.set("id_contraparte", String(f.id_contraparte));
  return qs.toString();
};

export function getNominacionDart(f: DartFiltro): Promise<DartRealizado> {
  return jsonFetch(`${NOM}/dart?${dartQs(f)}`);
}

export async function descargarNominacionDartXlsx(f: DartFiltro): Promise<void> {
  const res = await fetchAuth(`${NOM}/dart.xlsx?${dartQs(f)}`);
  if (!res.ok) throw new Error(await detalleDe(res));
  const blob = await res.blob();
  const objectUrl = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = objectUrl;
  const m = /filename="([^"]+)"/.exec(res.headers.get("Content-Disposition") ?? "");
  a.download = m ? m[1] : "DART_arbitraje.xlsx";
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(objectUrl);
}

// ---- Contabilidad: movimientos bancarios (mig 117) -------------------------

const MOVB = "/api/etrm/movimientos-bancarios";

export type MovimientoBancario = {
  id_movimiento: string;
  fecha_pago: string;
  concepto: string | null;
  factura_relacionada: string | null;
  tipo: "Ingreso" | "Egreso";
  inversion: "Abono" | "Retiro" | null;
  cuenta: string;
  monto: number | null;
  moneda: "MXN" | "USD";
  origen: "excel" | "manual";
  creado_por: string | null;
  creado_en: string;
  actualizado_por: string | null;
  actualizado_en: string;
};

/** Cuenta con saldos: saldo = inicial + ingresos − egresos; inversión = inicial + abonos − retiros. */
export type CuentaBancaria = {
  clave: string;
  moneda: "MXN" | "USD";
  saldo_inicial: number;
  inversion_inicial: number;
  ingresos: number;
  egresos: number;
  abonos: number;
  retiros: number;
  saldo: number;
  inversion: number;
};

export type MovimientoBancarioIn = {
  id_movimiento?: string;
  fecha_pago: string;
  concepto: string | null;
  factura_relacionada: string | null;
  tipo: string;
  inversion: string | null;
  cuenta: string;
  monto: number | null;
  moneda: string;
};

export type CargaMovimientos = {
  leidos: number;
  nuevos: number;
  actualizados: number;
  sin_cambio: number;
  errores: { renglon: number; id_movimiento: string; motivo: string }[];
  duplicados: string[];
  sin_monto: string[];
  sin_concepto: string[];
  ids_nuevos: string[];
  ids_actualizados: string[];
  aplicado: boolean;
};

export function getMovimientosBancarios(): Promise<{
  items: MovimientoBancario[];
  cuentas: CuentaBancaria[];
  tipo_cambio: TipoCambioConfig;
}> {
  return jsonFetch(MOVB);
}

/** Carga INCREMENTAL (upsert por ID Movimiento). confirmar=false = solo previa. */
export async function cargarMovimientosBancarios(
  archivo: File,
  confirmar: boolean,
): Promise<CargaMovimientos> {
  const fd = new FormData();
  fd.append("archivo", archivo);
  const res = await fetchAuth(`${MOVB}/cargar?confirmar=${confirmar}`, {
    method: "POST",
    body: fd,
  });
  if (!res.ok) throw new Error(await detalleDe(res));
  return res.json();
}

export function crearMovimientoBancario(m: MovimientoBancarioIn): Promise<MovimientoBancario> {
  return jsonPost(MOVB, m);
}

export function editarMovimientoBancario(
  id: string,
  m: MovimientoBancarioIn,
): Promise<MovimientoBancario> {
  return jsonPut(`${MOVB}/${encodeURIComponent(id)}`, m);
}

export function borrarMovimientoBancario(id: string): Promise<{ eliminado: boolean }> {
  return jsonDelete(`${MOVB}/${encodeURIComponent(id)}`);
}

/** Excel con las columnas de la carga; `ids` = los visibles con el filtro (null = todos). */
export async function descargarMovimientosBancariosXlsx(ids: string[] | null): Promise<void> {
  const res = await fetchAuth(`${MOVB}/xlsx`, {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ ids }),
  });
  if (!res.ok) throw new Error(await detalleDe(res));
  const objectUrl = URL.createObjectURL(await res.blob());
  const a = document.createElement("a");
  a.href = objectUrl;
  const m = /filename="([^"]+)"/.exec(res.headers.get("Content-Disposition") ?? "");
  a.download = m ? m[1] : "movimientos_bancarios.xlsx";
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(objectUrl);
}

export function borrarMovimientosBancarios(ids: string[]): Promise<{ eliminados: number }> {
  return jsonPost(`${MOVB}/borrar-varios`, { ids });
}

export function editarSaldosCuenta(
  clave: string,
  p: { saldo_inicial: number; inversion_inicial: number },
): Promise<{ actualizada: boolean }> {
  return jsonPut(`${MOVB}/cuentas/${encodeURIComponent(clave)}`, p);
}

// ---- Configuración → Tipo de Cambio (MXN por USD, mig 119) -----------------

export type TipoCambioConfig = {
  mxn_por_usd: number;
  actualizado_en: string | null;
  actualizado_por: string | null;
};

export function getTipoCambioConfig(): Promise<TipoCambioConfig> {
  return jsonFetch("/api/etrm/configuracion/tipo-cambio");
}

export function guardarTipoCambioConfig(mxn_por_usd: number): Promise<TipoCambioConfig> {
  return jsonPut("/api/etrm/configuracion/tipo-cambio", { mxn_por_usd });
}

// ---- Contabilidad: proyección de caja (mig 120) ----------------------------

const PCAJA = "/api/etrm/proyeccion-caja";

export type ProyeccionCaja = {
  id: number;
  moneda: "MXN" | "USD";
  fecha_pago: string;
  concepto: string | null;
  tipo: "Ingreso" | "Egreso";
  monto: number;
  origen: "excel" | "manual";
  actualizado_por: string | null;
  actualizado_en: string;
};

export type ProyeccionCajaResp = {
  moneda: "MXN" | "USD";
  /** = saldo_cuentas + inversion: Saldo Final de las cuentas de esa moneda (Movimientos bancarios) más sus inversiones. */
  saldo_inicial: number;
  /** Saldo Final de las cuentas bancarias de la moneda (sin inversiones). */
  saldo_cuentas: number;
  /** Inversiones de esas cuentas (inversión inicial + abonos − retiros). */
  inversion: number;
  /** Último movimiento bancario cargado; lo proyectado con fecha ≤ corte ya debería estar en bancos. */
  corte: string | null;
  cuentas: string[];
  /** Saldo final proyectado de cada moneda (para el total en MXN). */
  saldos_finales: { MXN: number; USD: number };
  tipo_cambio: TipoCambioConfig;
  items: ProyeccionCaja[];
};

export type ProyeccionCajaIn = {
  moneda: string;
  fecha_pago: string;
  concepto: string | null;
  tipo: string;
  monto: number | null;
};

export type CargaProyeccionCaja = {
  moneda: string;
  leidos: number;
  validos: number;
  errores: { renglon: number; motivo: string }[];
  existentes: number;
  ingresos: number;
  egresos: number;
  desde: string | null;
  hasta: string | null;
  aplicado: boolean;
  modo?: "agregar" | "reemplazar";
  borrados?: number;
};

export function getProyeccionCaja(moneda: string): Promise<ProyeccionCajaResp> {
  return jsonFetch(`${PCAJA}?moneda=${moneda}`);
}

/** confirmar=false = solo previa; con confirmar, modo agregar o reemplazar (esa moneda). */
export async function cargarProyeccionCaja(
  archivo: File,
  moneda: string,
  modo: "agregar" | "reemplazar",
  confirmar: boolean,
): Promise<CargaProyeccionCaja> {
  const fd = new FormData();
  fd.append("archivo", archivo);
  const res = await fetchAuth(
    `${PCAJA}/cargar?moneda=${moneda}&modo=${modo}&confirmar=${confirmar}`,
    { method: "POST", body: fd },
  );
  if (!res.ok) throw new Error(await detalleDe(res));
  return res.json();
}

export function crearProyeccionCaja(p: ProyeccionCajaIn): Promise<ProyeccionCaja> {
  return jsonPost(PCAJA, p);
}

export function editarProyeccionCaja(id: number, p: ProyeccionCajaIn): Promise<ProyeccionCaja> {
  return jsonPut(`${PCAJA}/${id}`, p);
}

export function borrarProyeccionCaja(id: number): Promise<{ eliminado: boolean }> {
  return jsonDelete(`${PCAJA}/${id}`);
}

export type ReporteCajaResp = {
  corte: string | null;
  tipo_cambio: TipoCambioConfig;
  /** Saldo real por cuenta al corte (saldo inicial + ingresos − egresos) e inversión. */
  cuentas: { clave: string; moneda: "MXN" | "USD"; saldo: number; inversion: number }[];
  /** Todo lo proyectado, ambas monedas, en orden de fecha. */
  items: ProyeccionCaja[];
};

export function getReporteCaja(): Promise<ReporteCajaResp> {
  return jsonFetch(`${PCAJA}/reporte`);
}

// ---- Contabilidad: garantías y préstamos Pant3ra (mig 121) -----------------

const GAR = "/api/etrm/garantias";

export type Garantia = {
  id_garantia: string;
  /** colocada = MPP la pone (CENACE, Iberdrola); recibida = un cliente la da a favor de MPP. */
  direccion: "colocada" | "recibida";
  contraparte: string;
  tipo: string | null;
  categoria: string | null;
  concepto: string | null;
  monto: number;
  moneda: "MXN" | "USD";
  fecha_inicio: string | null;
  fecha_vencimiento: string | null;
  vigencia: string | null;
  activa: boolean;
  actualizado_por: string | null;
  actualizado_en: string;
};

export type GarantiaIn = Omit<Garantia, "actualizado_por" | "actualizado_en">;

export type PrestamoPant3ra = {
  id: number;
  fecha: string;
  concepto: string | null;
  /** Préstamo suma al saldo deudor, Pago resta. */
  tipo: "Préstamo" | "Pago";
  monto: number;
  moneda: "MXN" | "USD";
  actualizado_por: string | null;
  actualizado_en: string;
};

export type PrestamoPant3raIn = Omit<PrestamoPant3ra, "id" | "actualizado_por" | "actualizado_en">;

export type GarantiasResp = {
  garantias: Garantia[];
  prestamos: PrestamoPant3ra[];
  tipo_cambio: TipoCambioConfig;
};

export function getGarantias(): Promise<GarantiasResp> {
  return jsonFetch(GAR);
}

export function crearGarantia(g: GarantiaIn): Promise<Garantia> {
  return jsonPost(GAR, g);
}

export function editarGarantia(id: string, g: GarantiaIn): Promise<Garantia> {
  return jsonPut(`${GAR}/${encodeURIComponent(id)}`, g);
}

export function borrarGarantia(id: string): Promise<{ eliminado: boolean }> {
  return jsonDelete(`${GAR}/${encodeURIComponent(id)}`);
}

export function crearPrestamoPant3ra(p: PrestamoPant3raIn): Promise<PrestamoPant3ra> {
  return jsonPost(`${GAR}/prestamos`, p);
}

export function editarPrestamoPant3ra(id: number, p: PrestamoPant3raIn): Promise<PrestamoPant3ra> {
  return jsonPut(`${GAR}/prestamos/${id}`, p);
}

export function borrarPrestamoPant3ra(id: number): Promise<{ eliminado: boolean }> {
  return jsonDelete(`${GAR}/prestamos/${id}`);
}

// ---------- P&L Contable (Reportes) -----------------------------------------

/** Una fila mensual del estado. Cada línea viene en dos bases: sin IVA (el P&L)
 *  y con IVA (lo facturado), con el sufijo `_iva`. */
export type PnlMes = {
  mes: string; // YYYY-MM
  fechas_cenace: number; // cuántas fechas de pago de CENACE cayeron en el mes
  ing_ee: number; ing_cel: number; ing_cons: number; ing_cenace: number; ing_proy: number;
  cos_ee: number; cos_cel: number; cos_ser: number; cos_cenace: number; cos_proy: number;
  total_ing: number; total_cos: number; utilidad: number;
  ing_ee_iva: number; ing_cel_iva: number; ing_cons_iva: number;
  ing_cenace_iva: number; ing_proy_iva: number;
  cos_ee_iva: number; cos_cel_iva: number; cos_ser_iva: number;
  cos_cenace_iva: number; cos_proy_iva: number;
  total_ing_iva: number; total_cos_iva: number; utilidad_iva: number;
};

export type PnlEmitida = {
  id: number;
  producto: string;
  folio: string;
  n_pago: number;
  desde: string;
  hasta: string;
  flp: string; // la fecha por la que entra al estado
  moneda: string;
  sin_iva: number; // ya convertido a MXN
  origen: "definitiva" | "preliminar" | "ajuste" | "periodo";
  nota: string;
};

export type PnlLinea = "ee_potencia" | "cel" | "servicios";

export type PnlRecibida = {
  id: number;
  fecha: string;
  emisor: string;
  receptor: string;
  linea: PnlLinea;
  moneda: string;
  mxn: number;
  nota: string;
};

export type PnlContable = {
  tc: number;
  meses: PnlMes[];
  emitidas: PnlEmitida[];
  recibidas: PnlRecibida[];
  meta: {
    n_emitidas: number;
    n_recibidas: number;
    n_servicios: number;
    n_proyeccion: number;
    cenace_hasta: string | null;
    mes_en_curso: string;
    sin_fecha: { id: number; sin_iva: number }[];
  };
};

export function getPnlContable(tc?: number): Promise<PnlContable> {
  return jsonFetch(`/api/etrm/pnl-contable${tc != null ? `?tc=${tc}` : ""}`);
}

export async function descargarPnlContableXlsx(tc: number, conIva: boolean): Promise<void> {
  const res = await fetchAuth(`/api/etrm/pnl-contable/xlsx?tc=${tc}&con_iva=${conIva}`, {
    credentials: "include",
  });
  if (!res.ok) {
    let detail = `${res.status} ${res.statusText}`;
    try {
      const j = await res.json();
      if (j?.detail) detail = j.detail;
    } catch {
      /* ignore */
    }
    throw new Error(detail);
  }
  const blob = await res.blob();
  const objectUrl = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = objectUrl;
  const disp = res.headers.get("Content-Disposition") ?? "";
  const m = /filename="([^"]+)"/.exec(disp);
  a.download = m ? m[1] : "pnl_contable.xlsx";
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(objectUrl);
}

// ---- Portafolio CEL / i-REC: permisionarios, generadores y bitácora (mig 124) ----

const PORTAFOLIO = "/api/etrm/portafolio";

export type MedioContacto = "llamada" | "correo" | "reunion" | "whatsapp" | "otro";
export type ProductoContacto = "cel" | "irec" | "ambos";
export type ResultadoContacto =
  | "pendiente" | "sin_respuesta" | "interesado" | "no_interesado" | "oferta_recibida" | "cerrado";

/** Resumen de la bitácora que viaja con cada ficha. */
export type ResumenSeguimiento = {
  seguimientos: number;
  ultimo: { fecha: string; medio: MedioContacto; resultado: ResultadoContacto; resumen: string; persona: string | null } | null;
  /** Próximo contacto pendiente (fecha_siguiente >= hoy), o null. */
  proximo: string | null;
};

export type Permisionario = ResumenSeguimiento & {
  id: number;
  razon_social: string;
  /** Clave G### del SIM (puede repetirse: un generador tiene varios permisos). */
  id_generador: string | null;
  nombre_generador: string | null;
  nombre_central: string | null;
  /** Nº de permiso CNE (E/1234/GEN/2018); único. */
  numero_permiso: string | null;
  marco_legal: string | null;
  modalidad: string | null;
  fecha_otorgamiento: string | null;
  capacidad_mw: number | null;
  generacion_gwh_anio: number | null;
  fecha_entrada_operacion: string | null;
  energetico_primario: string | null;
  tecnologia: string | null;
  entidad: string | null;
  pais_origen: string | null;
  /** null = N/A o por revisar. */
  es_renovable: boolean | null;
  /** Energía limpia; null = por revisar. */
  es_limpia: boolean | null;
  /** Calculado en el backend: LIE · MEM con energía limpia. */
  cel: boolean;
  /** Calculado en el backend: NO es LIE · MEM y la tecnología es eólica o solar. */
  irec: boolean;
  notas: string | null;
  activo: boolean;
  actualizado_por: string | null;
  actualizado_en: string;
};
export type PermisionarioIn = Omit<Permisionario, "id" | "cel" | "irec" | "actualizado_por" | "actualizado_en" | keyof ResumenSeguimiento>;

export type GeneradorPortafolio = ResumenSeguimiento & {
  id: number;
  clave: string;
  generador: string;
  centrales_generadoras: string | null;
  mw: number | null;
  tecnologia: string | null;
  nombre_comercial: string | null;
  contacto_comercial: string | null;
  notas: string | null;
  activo: boolean;
  actualizado_por: string | null;
  actualizado_en: string;
};
export type GeneradorPortafolioIn = Omit<GeneradorPortafolio, "id" | "actualizado_por" | "actualizado_en" | keyof ResumenSeguimiento>;

export type CatalogoGenerador = { clave: string; razon_social: string; en_portafolio: boolean };
export type CnePermiso = {
  numero: string; permisionario: string; alias_proyecto: string | null; estatus: string | null; fecha_acuse: string | null;
};

export type Seguimiento = {
  id: number;
  id_permisionario: number | null;
  id_generador: number | null;
  fecha: string;
  medio: MedioContacto;
  persona: string | null;
  producto: ProductoContacto;
  resultado: ResultadoContacto;
  resumen: string;
  siguiente_paso: string | null;
  fecha_siguiente: string | null;
  registrado_por: string | null;
  registrado_en: string;
};
export type SeguimientoIn = {
  tipo: "permisionario" | "generador";
  id_ref: number;
  fecha: string;
  medio: MedioContacto;
  persona: string | null;
  producto: ProductoContacto;
  resultado: ResultadoContacto;
  resumen: string;
  siguiente_paso: string | null;
  fecha_siguiente: string | null;
};

export function getPortafolio(): Promise<{ permisionarios: Permisionario[]; generadores: GeneradorPortafolio[] }> {
  return jsonFetch(PORTAFOLIO);
}
export function getCatalogoGeneradores(): Promise<CatalogoGenerador[]> {
  return jsonFetch(`${PORTAFOLIO}/catalogo-generadores`);
}
export function buscarCnePermisos(q: string): Promise<CnePermiso[]> {
  return jsonFetch(`${PORTAFOLIO}/cne-permisos?q=${encodeURIComponent(q)}`);
}
export function crearPermisionario(p: PermisionarioIn): Promise<Permisionario> {
  return jsonPost(`${PORTAFOLIO}/permisionarios`, p);
}
export function editarPermisionario(id: number, p: PermisionarioIn): Promise<Permisionario> {
  return jsonPut(`${PORTAFOLIO}/permisionarios/${id}`, p);
}
export function borrarPermisionario(id: number): Promise<{ eliminado: boolean }> {
  return jsonDelete(`${PORTAFOLIO}/permisionarios/${id}`);
}
export function crearGeneradorPortafolio(g: GeneradorPortafolioIn): Promise<GeneradorPortafolio> {
  return jsonPost(`${PORTAFOLIO}/generadores`, g);
}
export function editarGeneradorPortafolio(id: number, g: GeneradorPortafolioIn): Promise<GeneradorPortafolio> {
  return jsonPut(`${PORTAFOLIO}/generadores/${id}`, g);
}
export function borrarGeneradorPortafolio(id: number): Promise<{ eliminado: boolean }> {
  return jsonDelete(`${PORTAFOLIO}/generadores/${id}`);
}
export function getSeguimiento(tipo: "permisionario" | "generador", id: number): Promise<Seguimiento[]> {
  return jsonFetch(`${PORTAFOLIO}/seguimiento?tipo=${tipo}&id=${id}`);
}
export function crearSeguimiento(s: SeguimientoIn): Promise<Seguimiento> {
  return jsonPost(`${PORTAFOLIO}/seguimiento`, s);
}
export function editarSeguimiento(id: number, s: SeguimientoIn): Promise<Seguimiento> {
  return jsonPut(`${PORTAFOLIO}/seguimiento/${id}`, s);
}
export function borrarSeguimiento(id: number): Promise<{ eliminado: boolean }> {
  return jsonDelete(`${PORTAFOLIO}/seguimiento/${id}`);
}

// ---------- Auditoría (mig 126) ------------------------------------------

export type AuditoriaItem = {
  id: number;
  ts: string;
  usuario: string | null;
  tabla: string;
  tabla_label: string;
  operacion: "INSERT" | "UPDATE" | "DELETE";
  clave: string | null;
  cambios: Record<string, { antes: unknown; despues: unknown }> | null;
  fila: Record<string, unknown> | null;
};

export type AuditoriaFiltros = {
  tabla?: string;
  usuario?: string;
  operacion?: string;
  desde?: string;
  hasta?: string;
  q?: string;
  limite?: number;
  antes_de?: number;
};

export function getAuditoria(f: AuditoriaFiltros): Promise<{ items: AuditoriaItem[]; hay_mas: boolean }> {
  const p = new URLSearchParams();
  for (const [k, v] of Object.entries(f)) if (v !== undefined && v !== "" && v !== null) p.set(k, String(v));
  return jsonFetch(`/api/etrm/auditoria?${p.toString()}`);
}

export function getAuditoriaCatalogo(): Promise<{
  tablas: { tabla: string; label: string; n: number }[];
  usuarios: { usuario: string; n: number }[];
  labels: Record<string, string>;
}> {
  return jsonFetch("/api/etrm/auditoria/catalogo");
}

// ---------- Búsqueda global (Ctrl+K) --------------------------------------

export type BusquedaItem = { titulo: string; subtitulo: string | null; ruta: string };
export type BusquedaGrupo = { tipo: string; label: string; items: BusquedaItem[] };

export function buscarGlobal(q: string, signal?: AbortSignal): Promise<{ grupos: BusquedaGrupo[] }> {
  return jsonFetch(`/api/etrm/buscar?q=${encodeURIComponent(q)}`, { signal });
}

// ---- Favoritos del menú por usuario (mig 127) ----

/** `persistido` = false cuando la tabla aún no existe en la BD. */
export type Favoritos = { rutas: string[]; persistido: boolean };

export function getFavoritos(): Promise<Favoritos> {
  return jsonFetch("/api/etrm/favoritos");
}
export function guardarFavoritos(rutas: string[]): Promise<Favoritos> {
  return jsonPut("/api/etrm/favoritos", { rutas });
}

// ---- Reportes → Memoria de cálculo REA (public.monitoreo_rea) ----

export type ReaParticipante = {
  sistema: string;
  id_participante: string;
  nombre_pm: string | null;
  n: number;
  desde: string | null;
  hasta: string | null;
};

export type ReaSnapshotResumen = {
  momento: string;
  pct: number | null;
  semaforo: string | null;
  bullet_color: string | null;
  creado_en: string | null;
};

export type ReaPaso = {
  n: number;
  titulo: string;
  formula: string;
  valor: number | string | null;
  unidad: "MXN" | "%" | null;
  operandos?: Record<string, number | string | null>;
  racional: string;
  campo?: "mgp" | "pc" | "ppe" | "rea" | "pct";
};

export type ReaVerificacion = {
  clave: string;
  etiqueta: string;
  ok: boolean;
  calculado: number | string | null;
  publicado: number | string | null;
};

export type ReaMemoria = {
  snapshot: {
    momento: string;
    creado_en: string | null;
    sistema: string;
    id_participante: string;
    id_central: string | null;
    nombre_pm: string | null;
    rango: number | null;
    mgp: number | null;
    pc: number | null;
    ppe: number | null;
    rea: number | null;
    pct: number | null;
    semaforo: string | null;
    bullet_color: string | null;
    estado_bullet: string | null;
  };
  pasos: ReaPaso[];
  derivados: {
    posicion: "acreedora" | "deudora" | "neutra" | null;
    holgura_hasta_alerta: number | null;
    garantia_libre: number | null;
    umbral_pct: number;
  };
  verificaciones: ReaVerificacion[];
  anterior: {
    momento: string;
    mgp: number | null;
    pc: number | null;
    ppe: number | null;
    rea: number | null;
    pct: number | null;
    semaforo: string | null;
    delta: Record<"mgp" | "pc" | "ppe" | "rea" | "pct", number | null>;
  } | null;
  historia: ReaPunto[];
  desglose_pc: ReaDesglosePc;
  desglose_ppe: ReaDesglosePpe;
  fuente: { tabla: string; origen: string; etl: string };
};

/** Día operado que al corte del snapshot aún no tenía estado de cuenta. */
export type ReaDiaPendiente = {
  fecha_oper: string;
  con_edocta: boolean;
  fecha_emis: string | null;
  cargos: number | null;
  creditos: number | null;
  neto: number | null;
};

export type ReaDesglosePpe = {
  fecha_corte: string;
  ultimo_oper_emitido: string | null;
  dias: ReaDiaPendiente[];
  n_dias: number;
  n_con_edocta: number;
  ppe_publicada: number | null;
  ppe_por_dia: number | null;
  cargos_real: number | null;
  creditos_real: number | null;
  neto_real: number | null;
  diferencia: number | null;
  completo: boolean;
  referencia_7d: {
    dias: number; desde: string; hasta: string;
    cargos_dia: number; creditos_dia: number; neto_dia: number; neto_proyectado: number;
  } | null;
  formula_manual: string;
  formula_cpmcp: string;
  sin_cobertura: boolean;
  fuente: string;
  /** PPE esperada: perfil semanal empírico (media de las N semanas previas en
   *  el mismo día de la semana y hora de corrida). Estadístico, no la fórmula de CENACE. */
  esperada: ReaPpeEsperada;
};

export type ReaPpeEsperada = {
  dia: string;
  hora: number;
  esperada: number | null;
  sd: number | null;
  n: number;
  semanas: number;
  desde: string;
  hasta: string;
  publicada: number | null;
  diferencia: number | null;
  z: number | null;
  etiqueta: "típica" | "atípica" | "muy atípica" | null;
  perfil: { dia: string; dow: number; celdas: { hora: number; media: number | null; sd: number | null; n: number }[] }[];
  metodo: string;
};

/** Una factura del estado de cuenta vista por lado: cargo (CENACE cobra a MPP)
 *  o crédito (MPP cobra a CENACE). `signado` ya trae el signo con que entra a la PC. */
export type ReaPartidaPc = {
  fecha_oper: string;
  fecha_emis: string;
  fecha_pago: string;
  subcuenta: string;
  lado: "cargo" | "credito";
  tipos: string;
  n_facturas: number;
  subtotal: number;
  iva: number;
  monto: number;
  signado: number;
  emitido_hoy: boolean;
  vence_hoy: boolean;
  en_base: boolean;
  incluida: boolean;
};

export type ReaDesglosePc = {
  fecha_corte: string;
  regla: string;
  variante: "base" | "sin_emision_del_dia" | "con_cargos_del_dia" | "sin_emision_con_cargos_del_dia";
  variante_descripcion: string;
  cuadra: boolean;
  pc_publicado: number | null;
  total: number;
  diferencia: number | null;
  total_cargos: number;
  total_creditos: number;
  totales_variantes: Record<string, number>;
  n_partidas: number;
  partidas: ReaPartidaPc[];
  por_fecha_pago: { fecha_pago: string; cargos: number; creditos: number; neto: number; n: number }[];
  sin_cobertura: boolean;
  cobertura: { desde: string | null; hasta: string | null };
  fuente: string;
};

export function getReaParticipantes(): Promise<{ participantes: ReaParticipante[] }> {
  return jsonFetch("/api/etrm/memoria-rea/participantes");
}

export function getReaSnapshots(
  sistema: string,
  participante: string,
  desde?: string,
  hasta?: string,
): Promise<{ snapshots: ReaSnapshotResumen[] }> {
  const p = new URLSearchParams({ sistema, participante });
  if (desde) p.set("desde", desde);
  if (hasta) p.set("hasta", hasta);
  return jsonFetch(`/api/etrm/memoria-rea/snapshots?${p}`);
}

export function getReaMemoria(
  momento: string,
  sistema: string,
  participante: string,
): Promise<ReaMemoria> {
  const p = new URLSearchParams({ momento, sistema, participante });
  return jsonFetch(`/api/etrm/memoria-rea/snapshot?${p}`);
}

export async function descargarReaMemoriaXlsx(
  momento: string,
  sistema: string,
  participante: string,
): Promise<void> {
  const p = new URLSearchParams({ momento, sistema, participante });
  const res = await fetchAuth(`/api/etrm/memoria-rea/xlsx?${p}`, { credentials: "include" });
  if (!res.ok) throw new Error(await detalleDe(res));
  const blob = await res.blob();
  const objectUrl = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = objectUrl;
  const m = /filename="([^"]+)"/.exec(res.headers.get("Content-Disposition") ?? "");
  a.download = m ? m[1] : "memoria_rea.xlsx";
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(objectUrl);
}

export type ReaPunto = {
  momento: string;
  mgp: number | null;
  pc: number | null;
  ppe: number | null;
  rea: number | null;
  pct: number | null;
  semaforo: string | null;
};

export function getReaSerie(
  sistema: string,
  participante: string,
  desde?: string,
  hasta?: string,
): Promise<{ serie: ReaPunto[]; umbral_pct: number }> {
  const p = new URLSearchParams({ sistema, participante });
  if (desde) p.set("desde", desde);
  if (hasta) p.set("hasta", hasta);
  return jsonFetch(`/api/etrm/memoria-rea/serie?${p}`);
}
